/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { sb, upsertProfile, getProfileByEmail, savePlan, getActivePlan,
  logWeight, getWeightLogs, deleteWeightLog,
  uploadPhoto, getPhotos, deletePhoto,
  startWorkoutSession, finishWorkoutSession, logExerciseSets
} from "./lib/supabase";

// ═══════════════════════════════════════════════════════════════
// BRAND SYSTEM — GAINR Brand Guidelines v2.0
// ═══════════════════════════════════════════════════════════════
const C = {
  // Core palette
  grape:      "#7B4FFF",
  coral:      "#FF5C3A",
  sunshine:   "#FFD23F",
  mint:       "#3DFFC0",
  sky:        "#5BB8FF",
  cream:      "#FFF8F0",
  ink:        "#1A1A2E",
  // Functional
  bg:         "#FFF8F0",
  card:       "#FFFFFF",
  faint:      "#F0EBE3",
  border:     "rgba(26,26,46,0.09)",
  muted:      "#8B8BAD",
  // Dims
  grapeDim:   "rgba(123,79,255,0.10)",
  coralDim:   "rgba(255,92,58,0.10)",
  mintDim:    "rgba(61,255,192,0.12)",
  skyDim:     "rgba(91,184,255,0.12)",
  sunshineDim:"rgba(255,210,63,0.14)",
  roseDim:    "rgba(244,63,94,0.10)",
  rose:       "#F43F5E",
  amber:      "#F59700",
};

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Righteous&family=Nunito:wght@400;600;700;800;900&family=Nunito+Sans:wght@400;600;700;800&display=swap');`;

const GLOBAL = `
  ${FONTS}
  *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
  html, body { background:${C.bg}; overscroll-behavior:none; -webkit-font-smoothing:antialiased; }
  body { font-family:'Nunito','Nunito Sans',sans-serif; color:${C.ink}; }
  input, select, textarea { font-size:16px !important; font-family:inherit; }
  button { -webkit-tap-highlight-color:transparent; cursor:pointer; font-family:inherit; }
  @keyframes fadeUp   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn   { from{opacity:0}                             to{opacity:1} }
  @keyframes slideUp  { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin     { to{transform:rotate(360deg)} }
  @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:.45} }
  @keyframes popIn    { 0%{opacity:0;transform:scale(0.92)} 100%{opacity:1;transform:scale(1)} }
`;

// ═══════════════════════════════════════════════════════════════
// EVIDENCE-BASED DECISION ENGINE
// Sources: ACSM, ISSN, WHO, Helms et al., Morton meta-analysis
// ═══════════════════════════════════════════════════════════════
const ACT = { sedentary:1.2, light:1.375, moderate:1.55, active:1.725, very_active:1.9 };

function calcBMR(p) {
  const w=parseFloat(p.weight_kg)||70, h=parseFloat(p.height_cm)||170, a=parseInt(p.age)||25;
  return p.sex==="female" ? 10*w+6.25*h-5*a-161 : 10*w+6.25*h-5*a+5;
}
function calcTDEE(p) { return Math.round(calcBMR(p)*(ACT[p.activity_level]||1.55)); }

function calcMacros(p, goal, tdee) {
  const w = parseFloat(p.weight_kg)||70;
  const age = parseInt(p.age)||30;

  // Calorie targets — evidence-based, percentage-based (not flat numbers)
  let cal = tdee;
  if (goal==="cut")      cal = Math.round(tdee*0.84);  // ~16% deficit
  if (goal==="gain")     cal = Math.round(tdee*1.10);  // 10% conservative surplus (Helms et al.)
  if (goal==="recomp")   cal = Math.round(tdee*0.97);  // near-maintenance
  if (goal==="maintain") cal = tdee;
  cal = Math.max(p.sex==="female"?1200:1500, cal);

  // Protein — set first in g/kg (ISSN/ACSM)
  let proPerKg = goal==="maintain"?1.4 : goal==="cut"?2.0 : goal==="gain"?1.9 : 2.2;
  if (age>=50) proPerKg = Math.max(proPerKg,1.8);
  if (age>=40) proPerKg = Math.max(proPerKg,1.6);
  if (p.cycle_phase==="perimenopause"||p.cycle_phase==="menopause") proPerKg=Math.max(proPerKg,1.9);
  if (p.cycle_phase==="pcos") proPerKg=Math.max(proPerKg,1.9);
  const proteinG = Math.round(w*proPerKg);

  // Fat — floor 0.7 g/kg, capped at 30%
  const fatFloor = Math.round(w*0.7);
  let fatPct = goal==="cut"||goal==="recomp" ? 0.24 : 0.27;
  if (p.body_type==="endomorph"||p.body_type==="endo_meso") fatPct=Math.min(fatPct+0.02,0.30);
  if (p.body_type==="ectomorph") fatPct=Math.max(fatPct-0.02,0.20);
  const fatG = Math.max(fatFloor, Math.min(Math.round((cal*fatPct)/9), Math.round((cal*0.32)/9)));

  // Carbs fill remainder (floor 1.5 g/kg)
  const carbG = Math.max(Math.round(w*1.5), Math.round((cal-proteinG*4-fatG*9)/4));
  const actualCal = Math.round(proteinG*4 + carbG*4 + fatG*9);

  return {
    calories: actualCal, proteinG, carbG, fatG,
    proteinPerMeal: Math.round(proteinG/4),
    deficit: goal==="cut"  ? tdee-actualCal : null,
    surplus: goal==="gain" ? actualCal-tdee : null,
  };
}

// ═══════════════════════════════════════════════════════════════
// EXERCISE LIBRARY
// Source: ACSM Position Stand, Sports Medicine dose-response (2025)
// ═══════════════════════════════════════════════════════════════
const EX = {
  squat:        {name:"Back Squat",            muscle:"Quads/Glutes",      type:"compound"},
  rdl:          {name:"Romanian Deadlift",      muscle:"Hamstrings/Glutes", type:"compound"},
  trapbar:      {name:"Trap-Bar Deadlift",      muscle:"Posterior Chain",   type:"compound"},
  legpress:     {name:"Leg Press",              muscle:"Quads/Glutes",      type:"compound"},
  splitquat:    {name:"Bulgarian Split Squat",  muscle:"Quads/Glutes",      type:"compound"},
  hipthrust:    {name:"Hip Thrust",             muscle:"Glutes",            type:"compound"},
  hacksquat:    {name:"Hack Squat",             muscle:"Quads",             type:"compound"},
  frontsquat:   {name:"Front Squat",            muscle:"Quads",             type:"compound"},
  legcurl:      {name:"Lying Leg Curl",         muscle:"Hamstrings",        type:"isolation"},
  seatedlegcurl:{name:"Seated Leg Curl",        muscle:"Hamstrings",        type:"isolation"},
  legextension: {name:"Leg Extension",          muscle:"Quads",             type:"isolation"},
  calfpress:    {name:"Standing Calf Raise",    muscle:"Calves",            type:"isolation"},
  seatedcalf:   {name:"Seated Calf Raise",      muscle:"Calves",            type:"isolation"},
  bench:        {name:"Barbell Bench Press",    muscle:"Chest/Triceps",     type:"compound"},
  inclinedb:    {name:"Incline Dumbbell Press", muscle:"Upper Chest",       type:"compound"},
  ohp:          {name:"Overhead Press",         muscle:"Shoulders/Triceps", type:"compound"},
  dbohp:        {name:"Seated DB Shoulder Press",muscle:"Shoulders",        type:"compound"},
  machinechest: {name:"Machine Chest Press",    muscle:"Chest",             type:"compound"},
  cablefly:     {name:"Cable Fly",              muscle:"Chest",             type:"isolation"},
  lateralraise: {name:"Lateral Raise",          muscle:"Side Delts",        type:"isolation"},
  tricepspd:    {name:"Triceps Press-Down",     muscle:"Triceps",           type:"isolation"},
  tricepsext:   {name:"Triceps Extension",      muscle:"Triceps",           type:"isolation"},
  pullup:       {name:"Weighted Pull-Up",        muscle:"Lats/Biceps",      type:"compound"},
  pulldown:     {name:"Lat Pulldown",           muscle:"Lats/Biceps",       type:"compound"},
  barbellrow:   {name:"Barbell Row",            muscle:"Back/Biceps",       type:"compound"},
  cablerow:     {name:"Chest-Supported Row",    muscle:"Mid Back",          type:"compound"},
  machinerow:   {name:"Machine Row",            muscle:"Mid Back",          type:"compound"},
  onarmrow:     {name:"One-Arm Cable Row",      muscle:"Lats",              type:"compound"},
  reardelt:     {name:"Rear-Delt Raise",        muscle:"Rear Delts",        type:"isolation"},
  facepull:     {name:"Face Pull",              muscle:"Rear Delts/RC",     type:"isolation"},
  curlbar:      {name:"Barbell Curl",           muscle:"Biceps",            type:"isolation"},
  cablecurl:    {name:"Cable Curl",             muscle:"Biceps",            type:"isolation"},
  abwheel:      {name:"Ab Wheel Rollout",       muscle:"Core",              type:"isolation"},
  cablecrunch:  {name:"Cable Crunch",           muscle:"Core",              type:"isolation"},
  plank:        {name:"Plank",                  muscle:"Core",              type:"isolation"},
};

const HOME = {
  "Back Squat":"Goblet Squat","Barbell Bench Press":"DB Floor Press",
  "Weighted Pull-Up":"Inverted Row","Barbell Row":"DB Bent-Over Row",
  "Overhead Press":"DB Shoulder Press","Trap-Bar Deadlift":"DB Romanian Deadlift",
  "Hack Squat":"DB Goblet Squat","Lat Pulldown":"Band Pulldown",
  "Leg Press":"Bulgarian Split Squat","Machine Chest Press":"DB Press",
  "Machine Row":"DB Row","Triceps Press-Down":"DB Triceps Kickback",
  "Lying Leg Curl":"Nordic Curl","Leg Extension":"Reverse Lunge",
  "Cable Curl":"DB Curl","Cable Fly":"DB Fly","Cable Crunch":"Hollow Hold",
  "Face Pull":"Band Face Pull","One-Arm Cable Row":"Single-Arm DB Row",
  "Chest-Supported Row":"Incline DB Row",
};

// ═══════════════════════════════════════════════════════════════
// INJURY EXCLUSION ENGINE
// Sources: ACSM, JOSPT, Physio Network, Sports Medicine guidelines
// ═══════════════════════════════════════════════════════════════
const INJURY_EXCLUDE = {
  lower_back:  ["Romanian Deadlift","Trap-Bar Deadlift","Barbell Row","Ab Wheel Rollout","Good Morning","Back Squat"],
  knee:        ["Back Squat","Front Squat","Hack Squat","Bulgarian Split Squat","Leg Press","Leg Extension"],
  shoulder:    ["Overhead Press","Seated DB Shoulder Press","Upright Row","Behind-Neck Press","Barbell Bench Press","Incline Dumbbell Press"],
  wrist_elbow: ["Barbell Curl","Barbell Bench Press","Overhead Press","Ab Wheel Rollout","Triceps Extension","Triceps Press-Down"],
  hip_flexor:  ["Front Squat","Back Squat","Bulgarian Split Squat","Hip Thrust","Leg Press"],
};
const INJURY_SUB = {
  // lower back subs
  "Romanian Deadlift":    "Hip Thrust",
  "Trap-Bar Deadlift":    "Leg Press",
  "Barbell Row":          "Chest-Supported Row",
  "Ab Wheel Rollout":     "Plank",
  "Back Squat":           "Leg Press",
  "Front Squat":          "Leg Press",
  // knee subs
  "Hack Squat":           "Hip Thrust",
  "Bulgarian Split Squat":"Hip Thrust",
  "Leg Extension":        "Lying Leg Curl",
  // shoulder subs
  "Overhead Press":       "Lateral Raise",
  "Seated DB Shoulder Press":"Lateral Raise",
  "Barbell Bench Press":  "Machine Chest Press",
  "Incline Dumbbell Press":"Cable Fly",
  // wrist/elbow subs
  "Barbell Curl":         "Cable Curl",
  "Triceps Extension":    "Triceps Press-Down",
  // hip flexor subs
  "Hip Thrust":           "Lying Leg Curl",
};

function applyInjurySubs(exercises, injury) {
  if (!injury || injury==="none") return exercises;
  const excluded = INJURY_EXCLUDE[injury]||[];
  return exercises.map(ex=>{
    if (excluded.includes(ex.name)) {
      const subName = INJURY_SUB[ex.name]||null;
      if (!subName) return null; // drop if no sub available
      // Find the sub in EX library
      const subEntry = Object.values(EX).find(e=>e.name===subName);
      return subEntry ? {...ex,...subEntry,injuryMod:true} : null;
    }
    return ex;
  }).filter(Boolean);
}

function ex(key,sets,reps,rpe,rest,note="") {
  return {...EX[key],sets,reps,rpe,rest:`${rest} min`,note};
}

// ── Programme templates (Cut / Maintain FB / Maintain UL / Recomp / Gain)
const PROGRAMMES = {
  cut: {
    "Lower A": { day:"Mon",type:"Resistance",focus:"Lower — Strength",
      note:"Heavy compounds preserved. Rest 2–3 min on compounds. Trim accessories if energy is low.",
      exercises:[ex("squat",3,"4–6","7–8","2–3","75–85% 1RM"),ex("rdl",3,"6–8","7–8","2–3",""),
        ex("legpress",2,"8–10","8","1–2",""),ex("legcurl",2,"10–12","8–9","1–2",""),ex("calfpress",2,"12–15","8","1","")]},
    "Upper A": { day:"Tue",type:"Resistance",focus:"Upper — Strength",
      note:"Compounds first. 1–3 RIR — not every set to failure.",
      exercises:[ex("bench",3,"4–6","7–8","2–3","75–85% 1RM"),ex("pullup",3,"6–8","7–8","2–3",""),
        ex("inclinedb",2,"8–10","8","1–2",""),ex("cablerow",2,"8–10","8","1–2",""),
        ex("lateralraise",2,"12–15","8","1",""),ex("tricepspd",2,"10–15","8","1",""),ex("cablecurl",2,"10–15","8","1","")]},
    "Lower B": { day:"Thu",type:"Resistance",focus:"Lower — Volume",
      note:"Second lower day. Drop 1–2 accessories if fatigue is high.",
      exercises:[ex("trapbar",2,"3–5","8–9","2–3","78–88% 1RM"),ex("frontsquat",3,"6–8","8","2","Or hack squat"),
        ex("splitquat",2,"8–10","8","2","Per leg"),ex("seatedlegcurl",3,"10–12","8–9","1–2",""),
        ex("calfpress",2,"12–15","8","1",""),ex("abwheel",2,"8–12","8","1","")]},
    "Upper B": { day:"Fri",type:"Resistance",focus:"Upper — Volume",
      note:"Second upper day. Keep quality high.",
      exercises:[ex("ohp",3,"5–7","7–8","2–3",""),ex("barbellrow",3,"6–8","7–8","2",""),
        ex("machinechest",2,"8–12","8","1–2",""),ex("pulldown",2,"8–12","8","1–2",""),
        ex("cablefly",2,"12–15","8–9","1",""),ex("reardelt",2,"15–20","9","1",""),
        ex("curlbar",2,"10–15","8","1",""),ex("tricepsext",2,"10–15","8","1","")]},
  },
  maintain: {
    "Full Body A": { day:"Mon",type:"Resistance",focus:"Full Body — Strength",
      note:"Large muscles first. Consistency beats clever.",
      exercises:[ex("squat",3,"5–7","7–8","2–3","70–80% 1RM"),ex("bench",3,"5–7","7–8","2–3",""),
        ex("machinerow",3,"8–10","7–8","1–2",""),ex("legcurl",2,"10–12","8","1–2",""),
        ex("lateralraise",2,"12–15","8","1",""),ex("calfpress",2,"12–15","8","1","")]},
    "Full Body B": { day:"Wed",type:"Resistance",focus:"Full Body — Balanced",
      note:"Different patterns to Full Body A for complete stimulus.",
      exercises:[ex("rdl",3,"6–8","7–8","2–3",""),ex("ohp",3,"6–8","7–8","2–3",""),
        ex("pulldown",3,"8–10","8","1–2",""),ex("splitquat",2,"8–10","8","1–2","Per leg"),
        ex("cablecurl",2,"10–15","8","1",""),ex("tricepsext",2,"10–15","8","1","")]},
    "Full Body C": { day:"Fri",type:"Resistance",focus:"Full Body — Volume",
      note:"Slightly higher reps — sufficient to maintain muscle.",
      exercises:[ex("legpress",3,"8–10","8","2",""),ex("inclinedb",3,"8–10","8","1–2",""),
        ex("cablerow",3,"8–10","8","1–2",""),ex("hipthrust",2,"8–10","8","1–2",""),
        ex("reardelt",2,"12–20","9","1",""),ex("cablecrunch",2,"12–15","8","1","")]},
  },
  maintain_ul: {
    "Upper A": { day:"Mon",type:"Resistance",focus:"Upper — Strength",
      note:"Heavy compounds first. Minimum effective dose — quality over quantity.",
      exercises:[ex("bench",3,"5–7","7–8","2–3","70–80% 1RM"),ex("pullup",3,"6–8","7–8","2–3",""),
        ex("ohp",2,"6–8","7–8","2",""),ex("cablerow",2,"8–10","8","1–2",""),
        ex("lateralraise",2,"12–15","8","1",""),ex("cablecurl",2,"10–12","8","1",""),ex("tricepspd",2,"10–12","8","1","")]},
    "Lower A": { day:"Tue",type:"Resistance",focus:"Lower — Strength",
      note:"Squat and hinge pattern. Rest 2–3 min on compounds.",
      exercises:[ex("squat",3,"5–7","7–8","2–3","70–80% 1RM"),ex("rdl",3,"6–8","7–8","2–3",""),
        ex("legpress",2,"8–10","8","1–2",""),ex("legcurl",2,"10–12","8","1–2",""),
        ex("calfpress",2,"12–15","8","1",""),ex("cablecrunch",2,"10–15","8","1","")]},
    "Upper B": { day:"Thu",type:"Resistance",focus:"Upper — Volume",
      note:"Different angles to Upper A. Slightly higher reps.",
      exercises:[ex("inclinedb",3,"8–10","8","1–2",""),ex("barbellrow",3,"8–10","8","2",""),
        ex("machinechest",2,"10–12","8","1–2",""),ex("pulldown",2,"10–12","8","1–2",""),
        ex("reardelt",2,"12–20","9","1",""),ex("curlbar",2,"10–12","8","1",""),ex("tricepsext",2,"10–12","8","1","")]},
    "Lower B": { day:"Fri",type:"Resistance",focus:"Lower — Posterior & Glutes",
      note:"Hip-dominant. Complements squat-dominant Lower A.",
      exercises:[ex("hipthrust",3,"8–10","8","2","Full glute contraction"),ex("splitquat",3,"8–10","8","2","Per leg"),
        ex("hacksquat",2,"10–12","8","2","Or leg press"),ex("seatedlegcurl",2,"10–12","8","1–2",""),
        ex("seatedcalf",2,"12–15","8","1",""),ex("plank",2,"30–45s","8","1","")]},
  },
  recomp: {
    "Lower A": { day:"Mon",type:"Resistance",focus:"Lower — Hypertrophy",
      note:"Higher volume than cut. Strong anabolic stimulus essential. 1–3 RIR.",
      exercises:[ex("squat",4,"6–8","7–8","2–3","70–80% 1RM"),ex("rdl",3,"6–8","8","2",""),
        ex("legpress",3,"10–12","8","1–2",""),ex("legcurl",3,"10–12","8–9","1–2",""),ex("calfpress",3,"12–15","8","1","")]},
    "Upper A": { day:"Tue",type:"Resistance",focus:"Upper — Hypertrophy",
      note:"More sets than cut. Not a race to failure.",
      exercises:[ex("bench",4,"6–8","7–8","2–3",""),ex("barbellrow",4,"8–10","8","2",""),
        ex("inclinedb",3,"8–12","8","1–2",""),ex("pulldown",3,"8–12","8","1–2",""),
        ex("lateralraise",3,"12–20","8–9","1",""),ex("tricepsext",3,"10–15","8","1","")]},
    "Lower B": { day:"Thu",type:"Resistance",focus:"Lower — Posterior Chain",
      note:"If waist drops and lifts hold — the programme is working even if scale is flat.",
      exercises:[ex("trapbar",3,"5–8","8–9","2–3","Or hip thrust 3–4×5–8"),ex("hacksquat",3,"8–10","8","2",""),
        ex("splitquat",3,"10–12","8","2","Per leg"),ex("legextension",2,"12–15","8–9","1",""),
        ex("seatedlegcurl",3,"10–15","8–9","1–2",""),ex("plank",3,"30–45s","8","1","")]},
    "Upper B": { day:"Fri",type:"Resistance",focus:"Upper — Rear & Arms",
      note:"Performance markers are your key progress signal.",
      exercises:[ex("ohp",3,"6–8","7–8","2–3",""),ex("pullup",3,"8–10","8","2",""),
        ex("machinechest",3,"8–12","8","1–2",""),ex("cablerow",3,"8–12","8","1–2",""),
        ex("reardelt",3,"15–20","9","1",""),ex("curlbar",3,"10–15","8","1","")]},
  },
  gain: {
    "Push": { day:"Mon",type:"Resistance",focus:"Push — Chest, Shoulders, Triceps",
      note:"Largest stimulus for chest and anterior delts. Compounds before isolations always.",
      exercises:[ex("bench",4,"6–8","7–8","2–3","70–82% 1RM"),ex("inclinedb",3,"8–12","8","1–2",""),
        ex("dbohp",3,"8–10","8","2","Or barbell OHP"),ex("cablefly",2,"12–15","8–9","1",""),
        ex("lateralraise",4,"12–20","8–9","1","Priority — 4 sets"),ex("tricepspd",3,"10–15","8–9","1","")]},
    "Pull": { day:"Tue",type:"Resistance",focus:"Pull — Back, Rear Delts, Biceps",
      note:"Back is often undertrained. Full stretch and contraction every rep.",
      exercises:[ex("pullup",4,"6–10","8","2–3","Add weight"),ex("cablerow",4,"8–12","8","2","Chest supported"),
        ex("onarmrow",3,"10–12","8","1–2",""),ex("reardelt",3,"15–20","9","1",""),
        ex("facepull",2,"12–20","8","1",""),ex("curlbar",4,"10–15","8–9","1","Priority biceps volume")]},
    "Legs": { day:"Wed",type:"Resistance",focus:"Legs — Quads, Hamstrings, Glutes, Calves",
      note:"Most important session for overall physique. Never skip.",
      exercises:[ex("squat",4,"6–8","7–8","2–3",""),ex("rdl",3,"6–8","8","2",""),
        ex("legpress",3,"10–15","8–9","2",""),ex("legextension",2,"12–15","8–9","1",""),
        ex("legcurl",3,"10–15","8–9","1–2",""),ex("seatedcalf",4,"10–20","9","1","Full stretch")]},
    "Upper": { day:"Fri",type:"Resistance",focus:"Upper — Balanced Hypertrophy",
      note:"Machine-friendly — accumulates volume with less CNS fatigue.",
      exercises:[ex("inclinedb",3,"8–10","8","2",""),ex("machinerow",3,"8–10","8","1–2",""),
        ex("pulldown",3,"10–12","8","1–2",""),ex("machinechest",3,"10–12","8","1–2",""),
        ex("lateralraise",3,"15–20","9","1",""),ex("tricepsext",2,"10–15","8","1",""),ex("cablecurl",2,"10–15","8","1","")]},
    "Lower B": { day:"Sat",type:"Resistance",focus:"Lower — Glutes & Posterior Chain",
      note:"Hip-dominant. Drop 1–2 sets if recovery or motivation drops.",
      exercises:[ex("hipthrust",4,"6–10","8","2","Full glute contraction"),ex("frontsquat",3,"8–10","8","2","Or hack squat"),
        ex("splitquat",3,"10–12","8","2","Per leg"),ex("seatedlegcurl",3,"10–15","8–9","1–2",""),
        ex("calfpress",3,"12–20","9","1",""),ex("cablecrunch",2,"12–15","8","1","")]},
  },
};

// Periodisation blocks
const BLOCKS = [
  {name:"Foundation", weeks:3, focus:"Technique & base volume",    rpe:"6–7", setMod:0.85},
  {name:"Hypertrophy",weeks:4, focus:"Volume & muscle growth",      rpe:"7–8", setMod:1.0},
  {name:"Strength",   weeks:3, focus:"Load & progressive overload", rpe:"8–9", setMod:0.9},
  {name:"Deload",     weeks:1, focus:"Recovery & supercompensation",rpe:"4–5", setMod:0.5},
];
function getCurrentBlock(weekNum) {
  const cycle=11; let w=(weekNum-1)%cycle;
  for (const b of BLOCKS) { if(w<b.weeks) return {...b,weekInBlock:w+1}; w-=b.weeks; }
  return {...BLOCKS[0],weekInBlock:1};
}

function buildSchedule(profile) {
  const goal=profile.goal||"maintain", days=parseInt(profile.training_days)||3;
  const isHome=profile.training_type==="home", hist=profile.training_history||"beginner";
  const ALL=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  let progKey = goal;
  if (goal==="maintain"&&days>=4) progKey="maintain_ul";
  const prog = PROGRAMMES[progKey]||PROGRAMMES.maintain;
  const keys  = Object.keys(prog);

  const DAY_MAPS = {2:["Mon","Thu"],3:["Mon","Wed","Fri"],4:["Mon","Tue","Thu","Fri"],5:["Mon","Tue","Wed","Fri","Sat"],6:["Mon","Tue","Wed","Thu","Fri","Sat"]};
  const usable  = Math.min(days,keys.length);
  const assigned = DAY_MAPS[usable]||DAY_MAPS[3];
  const usedKeys = keys.slice(0,assigned.length);

  return usedKeys.map((key,i) => {
    const s = prog[key];
    let exercises = s.exercises.map(ex => ({
      ...ex, name: isHome&&HOME[ex.name]?HOME[ex.name]:ex.name
    }));
    if (hist==="beginner") exercises=exercises.map(e=>({...e,sets:Math.max(1,e.sets-1)}));
    if (hist==="advanced")  exercises=exercises.map(e=>({...e,sets:e.type==="compound"?e.sets+1:e.sets}));
    // Apply injury substitutions
    exercises = applyInjurySubs(exercises, profile.injuries);
    return {day:assigned[i],label:key,focus:s.focus,note:s.note,type:s.type,exercises};
  }).sort((a,b)=>ALL.indexOf(a.day)-ALL.indexOf(b.day));
}

// ═══════════════════════════════════════════════════════════════
// SHARED UI PRIMITIVES
// ═══════════════════════════════════════════════════════════════
function Logo({size=20}) {
  const s=size;
  return (
    <div style={{display:"flex",alignItems:"center",gap:9}}>
      <div style={{position:"relative",width:s*2,height:s*2,flexShrink:0}}>
        <div style={{position:"absolute",top:s*0.16,left:s*0.08,width:s*2,height:s*2,
          background:C.coral,borderRadius:s*0.44}}/>
        <div style={{position:"absolute",top:0,left:0,width:s*2,height:s*2,
          background:C.ink,borderRadius:s*0.44,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <span style={{fontFamily:"'Righteous',cursive",fontSize:s*1.25,color:C.sunshine,lineHeight:1}}>G</span>
        </div>
      </div>
      <span style={{fontFamily:"'Righteous',cursive",fontSize:s*1.1,color:C.ink,letterSpacing:"-0.01em"}}>
        GAIN<span style={{color:C.coral}}>R</span>
      </span>
    </div>
  );
}

function Btn({children,onClick,variant="primary",disabled,loading,fullWidth,small,style:s={}}) {
  const v = {
    primary:   {background:C.coral,  color:"#fff", boxShadow:`4px 4px 0px ${C.ink}`},
    secondary: {background:C.grape,  color:"#fff", boxShadow:`4px 4px 0px ${C.ink}`},
    ghost:     {background:"transparent",color:C.muted,border:`1.5px solid ${C.border}`,boxShadow:"none"},
    ink:       {background:C.ink,    color:C.sunshine,boxShadow:`4px 4px 0px ${C.coral}`},
  }[variant]||{};
  return (
    <button onClick={disabled||loading?undefined:onClick}
      style={{padding:small?"10px 18px":"14px 24px",borderRadius:12,border:"none",fontWeight:800,
        fontSize:small?13:15,transition:"all 0.18s",width:fullWidth?"100%":"auto",
        opacity:disabled||loading?0.5:1,cursor:disabled||loading?"not-allowed":"pointer",
        display:"flex",alignItems:"center",justifyContent:"center",gap:8,...v,...s}}>
      {loading&&<div style={{width:15,height:15,border:"2px solid rgba(255,255,255,0.3)",
        borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>}
      {children}
    </button>
  );
}

function Input({label,value,onChange,type="text",placeholder,unit,hint}) {
  return (
    <div>
      {label&&<label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
        color:C.muted,display:"block",marginBottom:6,fontFamily:"'Nunito Sans',sans-serif"}}>{label}</label>}
      <div style={{position:"relative"}}>
        <input type={type} value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
          style={{width:"100%",padding:`13px ${unit?"40px":"14px"} 13px 14px`,borderRadius:12,
            border:`1.5px solid ${value?C.grape:C.border}`,background:C.bg,color:C.ink,
            fontSize:15,fontWeight:700,outline:"none",transition:"border-color 0.2s"}}/>
        {unit&&<span style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",
          fontSize:11,fontWeight:700,color:C.muted,fontFamily:"'Nunito Sans',sans-serif"}}>{unit}</span>}
      </div>
      {hint&&<div style={{fontSize:11,color:C.muted,marginTop:4,fontFamily:"'Nunito Sans',sans-serif"}}>{hint}</div>}
    </div>
  );
}

function Select({label,value,onChange,options,placeholder}) {
  return (
    <div>
      {label&&<label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
        color:C.muted,display:"block",marginBottom:6,fontFamily:"'Nunito Sans',sans-serif"}}>{label}</label>}
      <select value={value||""} onChange={e=>onChange(e.target.value)}
        style={{width:"100%",padding:"13px 36px 13px 14px",borderRadius:12,
          border:`1.5px solid ${value?C.grape:C.border}`,background:C.bg,color:value?C.ink:C.muted,
          fontSize:14,fontWeight:600,outline:"none",appearance:"none",
          backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238B8BAD' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
          backgroundRepeat:"no-repeat",backgroundPosition:"right 14px center"}}>
        <option value="">{placeholder||"Select…"}</option>
        {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Card({children,style:s={},accent}) {
  return (
    <div style={{background:C.card,border:`1.5px solid ${accent||C.border}`,borderRadius:16,
      padding:"18px 16px",boxShadow:"2px 2px 0px rgba(26,26,46,0.06)",...s}}>
      {children}
    </div>
  );
}

function Pill({label,color}) {
  return <span style={{fontSize:10,fontWeight:700,color,background:`${color}18`,
    border:`1px solid ${color}30`,padding:"2px 9px",borderRadius:20,display:"inline-block",
    fontFamily:"'Nunito Sans',sans-serif",letterSpacing:"0.05em"}}>{label}</span>;
}

function SLabel({children}) {
  return <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",textTransform:"uppercase",
    color:C.muted,marginBottom:10,fontFamily:"'Nunito Sans',sans-serif"}}>{children}</div>;
}

function Spinner() {
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      minHeight:"50vh",gap:14}}>
      <div style={{width:36,height:36,border:`3px solid ${C.faint}`,borderTopColor:C.coral,
        borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
      <div style={{fontSize:13,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontWeight:600}}>Building your plan…</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREEN: LANDING — email + password auth (sign up / sign in)
// ═══════════════════════════════════════════════════════════════
function LandingScreen({onSignedIn}) {
  const [mode,     setMode]     = useState("signup"); // "signup" | "signin" | "reset"
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [error,    setError]    = useState("");
  const [info,     setInfo]     = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);

  function reset() { setError(""); setInfo(""); }

  async function handleSubmit() {
    reset();
    if (!email || !email.includes("@")) { setError("Please enter a valid email address."); return; }

    if (mode==="reset") {
      setLoading(true);
      const { error:e } = await sb.auth.resetPasswordForEmail(email, {
        redirectTo: "https://gainr.fitness"
      });
      setLoading(false);
      if (e) setError(e.message);
      else setInfo("Password reset email sent — check your inbox.");
      return;
    }

    if (!password || password.length < 8) { setError("Password must be at least 8 characters."); return; }

    if (mode==="signup") {
      if (password !== confirm) { setError("Passwords don't match."); return; }
      setLoading(true);
      const { data, error:e } = await sb.auth.signUp({ email, password });
      setLoading(false);
      if (e) { setError(e.message); return; }
      if (data?.user && !data.session) {
        // Email confirmation required
        setLoading(false);
        setInfo("Account created! Check your email to confirm your account, then sign in.");
        setMode("signin");
        setPassword(""); setConfirm("");
      } else if (data?.session) {
        // Auto-confirmed — onAuthStateChange will handle navigation
        // setLoading clears when screen changes
      }
      return;
    }

    if (mode==="signin") {
      setLoading(true);
      const { data, error:e } = await sb.auth.signInWithPassword({ email, password });
      if (e) {
        setLoading(false);
        setError(e.message==="Invalid login credentials" ? "Incorrect email or password." : e.message);
        return;
      }
      // Don't call onSignedIn — onAuthStateChange handles the session automatically
      // setLoading will clear when the screen changes
    }
  }

  const LogoBar = () => (
    <div style={{display:"flex",justifyContent:"center",marginBottom:44}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{position:"relative",width:44,height:44}}>
          <div style={{position:"absolute",top:4,left:3,width:44,height:44,background:C.coral,borderRadius:11}}/>
          <div style={{position:"absolute",top:0,left:0,width:44,height:44,background:C.ink,
            border:"1.5px solid rgba(255,255,255,0.12)",borderRadius:11,
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            <span style={{fontFamily:"'Righteous',cursive",fontSize:24,color:C.sunshine}}>G</span>
          </div>
        </div>
        <span style={{fontFamily:"'Righteous',cursive",fontSize:28,color:"#fff",letterSpacing:"-0.01em"}}>
          GAIN<span style={{color:C.coral}}>R</span>
        </span>
      </div>
    </div>
  );

  const inputStyle = (val,err) => ({
    width:"100%",padding:"13px 16px",borderRadius:12,
    border:`1.5px solid ${err?"rgba(244,63,94,0.6)":val?"rgba(123,79,255,0.6)":"rgba(255,255,255,0.12)"}`,
    background:"rgba(255,255,255,0.08)",color:"#fff",fontSize:16,fontWeight:700,
    outline:"none",fontFamily:"'Nunito',sans-serif",marginBottom:12,boxSizing:"border-box",
  });

  return (
    <div style={{minHeight:"100vh",background:C.ink,display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",padding:"24px 20px",
      position:"relative",overflow:"hidden"}}>
      <style>{GLOBAL}</style>

      {/* Static blobs */}
      {[
        {size:320,color:C.grape,top:"-100px",left:"-80px"},
        {size:200,color:C.coral,bottom:"40px",right:"-40px"},
        {size:140,color:C.mint, top:"35%",  left:"60%"},
      ].map((b,i)=>(
        <div key={i} style={{position:"absolute",width:b.size,height:b.size,borderRadius:"50%",
          background:b.color,opacity:0.18,top:b.top,left:b.left,bottom:b.bottom,right:b.right,
          filter:"blur(40px)",pointerEvents:"none"}}/>
      ))}

      <div style={{width:"100%",maxWidth:400,position:"relative",zIndex:1,animation:"slideUp 0.6s ease both"}}>
        <LogoBar/>

        {/* Headline — only on signup */}
        {mode==="signup"&&(
          <div style={{textAlign:"center",marginBottom:32}}>
            <h1 style={{fontFamily:"'Righteous',cursive",fontSize:32,color:"#fff",
              letterSpacing:"-0.02em",lineHeight:1.15,marginBottom:12}}>
              You've got the drive.<br/>
              <span style={{color:C.sunshine}}>We've got the plan.</span>
            </h1>
            <p style={{fontSize:14,color:"rgba(255,255,255,0.5)",lineHeight:1.7,
              fontFamily:"'Nunito',sans-serif",fontWeight:600}}>
              Evidence-based macros and training — built around you.
            </p>
          </div>
        )}

        {/* Card */}
        <div style={{background:"rgba(255,255,255,0.06)",border:"1.5px solid rgba(255,255,255,0.10)",
          borderRadius:20,padding:"28px 24px",backdropFilter:"blur(10px)"}}>

          {/* Mode tabs */}
          {mode!=="reset"&&(
            <div style={{display:"flex",gap:4,background:"rgba(255,255,255,0.08)",
              borderRadius:12,padding:4,marginBottom:24}}>
              {[{id:"signup",l:"Create Account"},{id:"signin",l:"Sign In"}].map(m=>(
                <button key={m.id} onClick={()=>{setMode(m.id);reset();}}
                  style={{flex:1,padding:"10px",borderRadius:9,border:"none",cursor:"pointer",
                    fontWeight:800,fontSize:13,fontFamily:"'Nunito',sans-serif",
                    transition:"all 0.2s",
                    background:mode===m.id?"#fff":"transparent",
                    color:mode===m.id?C.ink:"rgba(255,255,255,0.5)",
                    boxShadow:mode===m.id?"0 1px 4px rgba(0,0,0,0.2)":"none"}}>
                  {m.l}
                </button>
              ))}
            </div>
          )}

          {/* Reset mode heading */}
          {mode==="reset"&&(
            <div style={{marginBottom:20}}>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:20,color:"#fff",marginBottom:6}}>
                Reset Password
              </div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.6,
                fontFamily:"'Nunito Sans',sans-serif"}}>
                Enter your email and we'll send you a reset link.
              </div>
            </div>
          )}

          {/* Error / info banners */}
          {error&&(
            <div style={{background:"rgba(244,63,94,0.15)",border:"1px solid rgba(244,63,94,0.4)",
              borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,
              color:"#f87171",fontWeight:600,lineHeight:1.5}}>
              {error}
            </div>
          )}
          {info&&(
            <div style={{background:"rgba(61,255,192,0.12)",border:"1px solid rgba(61,255,192,0.35)",
              borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,
              color:C.mint,fontWeight:600,lineHeight:1.5}}>
              {info}
            </div>
          )}

          {/* Email */}
          <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",
            color:"rgba(255,255,255,0.45)",display:"block",marginBottom:8,
            fontFamily:"'Nunito Sans',sans-serif"}}>Email</label>
          <input type="email" value={email}
            onChange={e=>{setEmail(e.target.value);reset();}}
            placeholder="you@example.com"
            onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
            style={{...inputStyle(email,false)}}/>

          {/* Password */}
          {mode!=="reset"&&(
            <>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",
                color:"rgba(255,255,255,0.45)",display:"block",marginBottom:8,
                fontFamily:"'Nunito Sans',sans-serif"}}>Password</label>
              <div style={{position:"relative",marginBottom:12}}>
                <input type={showPw?"text":"password"} value={password}
                  onChange={e=>{setPassword(e.target.value);reset();}}
                  placeholder={mode==="signup"?"Min. 8 characters":"Your password"}
                  onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                  style={{...inputStyle(password,false),marginBottom:0,paddingRight:44}}/>
                <button onClick={()=>setShowPw(v=>!v)}
                  style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",
                    background:"none",border:"none",cursor:"pointer",
                    fontSize:16,color:"rgba(255,255,255,0.4)"}}>
                  {showPw?"🙈":"👁"}
                </button>
              </div>
            </>
          )}

          {/* Confirm password — signup only */}
          {mode==="signup"&&(
            <>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",
                color:"rgba(255,255,255,0.45)",display:"block",marginBottom:8,
                fontFamily:"'Nunito Sans',sans-serif"}}>Confirm Password</label>
              <input type={showPw?"text":"password"} value={confirm}
                onChange={e=>{setConfirm(e.target.value);reset();}}
                placeholder="Repeat your password"
                onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                style={{...inputStyle(confirm,false)}}/>
            </>
          )}

          {/* CTA button */}
          <button onClick={handleSubmit} disabled={loading}
            style={{width:"100%",padding:"15px",borderRadius:12,border:"none",
              background:loading?"rgba(255,92,58,0.5)":C.coral,color:"#fff",
              fontWeight:900,fontSize:15,cursor:loading?"not-allowed":"pointer",
              fontFamily:"'Nunito',sans-serif",
              boxShadow:loading?"none":`4px 4px 0px rgba(255,255,255,0.15)`,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
              transition:"all 0.18s"}}>
            {loading&&<div style={{width:16,height:16,border:"2px solid rgba(255,255,255,0.3)",
              borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>}
            {mode==="signup"&&"Create Account & Build My Plan →"}
            {mode==="signin"&&"Sign In →"}
            {mode==="reset"&&"Send Reset Link →"}
          </button>

          {/* Forgot password / back links */}
          <div style={{marginTop:16,textAlign:"center"}}>
            {mode==="signin"&&(
              <button onClick={()=>{setMode("reset");reset();}}
                style={{background:"none",border:"none",cursor:"pointer",
                  fontSize:12,color:"rgba(255,255,255,0.4)",fontWeight:600,
                  fontFamily:"'Nunito Sans',sans-serif",textDecoration:"underline"}}>
                Forgot password?
              </button>
            )}
            {mode==="reset"&&(
              <button onClick={()=>{setMode("signin");reset();}}
                style={{background:"none",border:"none",cursor:"pointer",
                  fontSize:12,color:"rgba(255,255,255,0.4)",fontWeight:600,
                  fontFamily:"'Nunito Sans',sans-serif",textDecoration:"underline"}}>
                ← Back to Sign In
              </button>
            )}
          </div>
        </div>

        {/* Evidence badges */}
        <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap",marginTop:22}}>
          {["ACSM","ISSN","WHO","NICE"].map(t=>(
            <span key={t} style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.3)",
              border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"3px 10px",
              fontFamily:"'Nunito Sans',sans-serif",letterSpacing:"0.08em"}}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ONBOARDING CONSTANTS
// ═══════════════════════════════════════════════════════════════
const OB_GOALS = [
  {value:"maintain",label:"Maintain",  icon:"◈",color:C.mint,   desc:"Build healthy habits, stable weight"},
  {value:"cut",     label:"Cut",       icon:"▽",color:C.coral,  desc:"Lose fat, preserve lean muscle"},
  {value:"gain",    label:"Gain",      icon:"△",color:C.sky,    desc:"Build muscle with a controlled surplus"},
  {value:"recomp",  label:"Recomp",    icon:"⟳",color:C.grape,  desc:"Lose fat & build muscle simultaneously"},
];
const OB_STEPS = [
  {id:"goal",     title:"Your Goal",         sub:"What are you training for?",             icon:"🎯"},
  {id:"bio",      title:"Your Stats",        sub:"Personalises your macro targets",         icon:"📏"},
  {id:"lifestyle",title:"Training Setup",    sub:"Builds your exercise programme",          icon:"🏋️"},
  {id:"diet",     title:"Macro Preferences", sub:"How you want your nutrition handled",     icon:"🥗"},
];
const ACT_OPTS = [
  {value:"sedentary",  label:"Sedentary",   sub:"Desk job · fewer than 5,000 steps/day · no structured exercise"},
  {value:"light",      label:"Light",        sub:"1–2 light sessions/week · moderately active day job"},
  {value:"moderate",   label:"Moderate",     sub:"3–4 sessions/week · some activity outside the gym"},
  {value:"active",     label:"Active",       sub:"Hard training 5×/week · physically demanding job"},
  {value:"very_active",label:"Very Active",  sub:"Twice daily training · elite athlete · labour-intensive work"},
];
const HIST_OPTS = [
  {value:"beginner",    label:"Beginner",     sub:"< 1 year · still learning movement patterns · building base fitness"},
  {value:"intermediate",label:"Intermediate", sub:"1–3 years · comfortable with compound lifts · training consistently"},
  {value:"advanced",    label:"Advanced",     sub:"3+ years · training with periodisation and progressive overload"},
];
const CYCLE_OPTS = [
  {value:"regular",            label:"Regular Menstrual Cycle"},
  {value:"pcos",               label:"PCOS"},
  {value:"perimenopause",      label:"Perimenopause"},
  {value:"premature_menopause",label:"Premature Menopause / POI"},
  {value:"menopause",          label:"Menopause / Post-Menopause"},
];

function OnboardingScreen({email,onComplete}) {
  const [step,setStep]=useState(0);
  const [p,setP]=useState({
    goal:"",age:"",sex:"",height_cm:"",weight_kg:"",goal_weight_kg:"",
    cycle_phase:"",body_type:"",activity_level:"",training_history:"",
    training_days:"3",training_type:"gym",training_style:"split",
    injuries:"none",injury_detail:"",macro_mode:"auto",
    custom_calories:"",custom_protein:"",custom_carbs:"",custom_fats:"",
  });
  const up=(k,v)=>setP(prev=>({...prev,[k]:v}));

  useEffect(()=>{ window.scrollTo({top:0,behavior:"instant"}); },[step]);

  const canNext = ()=>{
    if(step===0) return !!p.goal;
    if(step===1) return !!(p.age&&p.sex&&p.height_cm&&p.weight_kg);
    if(step===2) return !!(p.activity_level&&p.training_history&&p.training_days&&p.training_type);
    return true;
  };

  function handleFinish() {
    onComplete({...p,email,
      age:parseInt(p.age), height_cm:parseFloat(p.height_cm),
      weight_kg:parseFloat(p.weight_kg), goal_weight_kg:parseFloat(p.goal_weight_kg)||null,
      training_days:parseInt(p.training_days),
    });
  }

  const progress=((step+1)/OB_STEPS.length)*100;
  const cur=OB_STEPS[step];

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"'Nunito',sans-serif"}}>
      <style>{GLOBAL}</style>

      {/* Header */}
      <div style={{background:"rgba(255,248,240,0.96)",backdropFilter:"blur(16px)",
        padding:"12px 20px",borderBottom:`1px solid ${C.border}`,
        display:"flex",alignItems:"center",justifyContent:"space-between",
        position:"sticky",top:0,zIndex:10}}>
        <Logo size={16}/>
        <span style={{fontSize:11,fontWeight:700,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",
          letterSpacing:"0.06em"}}>{step+1} of {OB_STEPS.length}</span>
      </div>

      {/* Progress */}
      <div style={{height:4,background:C.faint}}>
        <div style={{height:"100%",background:`linear-gradient(90deg,${C.coral},${C.grape})`,
          width:`${progress}%`,transition:"width 0.4s ease",borderRadius:99}}/>
      </div>

      {/* Content */}
      <div style={{maxWidth:500,margin:"0 auto",padding:"28px 20px 160px",
        animation:"slideUp 0.3s ease both"}} key={step}>

        {/* Step header */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
          <div style={{width:46,height:46,borderRadius:13,background:C.ink,flexShrink:0,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,
            boxShadow:`2px 2px 0px ${C.coral}`}}>{cur.icon}</div>
          <div>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",
              color:C.muted,marginBottom:2,fontFamily:"'Nunito Sans',sans-serif"}}>Step {step+1} of {OB_STEPS.length}</div>
            <div style={{fontFamily:"'Righteous',cursive",fontSize:26,color:C.ink,lineHeight:1}}>{cur.title}</div>
          </div>
        </div>
        <p style={{fontSize:13,color:C.muted,marginBottom:28,marginLeft:58,lineHeight:1.6}}>{cur.sub}</p>

        {/* ── STEP 0: GOAL ── */}
        {step===0&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {OB_GOALS.map(g=>{
              const sel=p.goal===g.value;
              return (
                <button key={g.value} onClick={()=>up("goal",g.value)}
                  style={{display:"flex",flexDirection:"column",gap:8,padding:"20px 14px",
                    borderRadius:16,border:"none",textAlign:"left",cursor:"pointer",
                    background:sel?`${g.color}12`:C.card,
                    outline:`2.5px solid ${sel?g.color:C.border}`,
                    boxShadow:sel?`4px 4px 0px ${C.ink}`:"2px 2px 0px rgba(26,26,46,0.05)",
                    transition:"all 0.18s"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontFamily:"'Righteous',cursive",fontSize:19,color:sel?g.color:C.ink}}>{g.label}</span>
                    <span style={{fontSize:22,opacity:sel?1:0.5}}>{g.icon}</span>
                  </div>
                  <span style={{fontSize:12,color:sel?g.color:C.muted,lineHeight:1.5,fontWeight:600}}>{g.desc}</span>
                  {sel&&<span style={{fontSize:10,fontWeight:800,color:g.color,
                    background:`${g.color}18`,borderRadius:20,padding:"2px 8px",alignSelf:"flex-start"}}>Selected ✓</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* ── STEP 1: BIO ── */}
        {step===1&&(
          <div style={{display:"flex",flexDirection:"column",gap:16,animation:"fadeUp 0.3s ease both"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Input label="Age" value={p.age} onChange={v=>up("age",v)} type="number" placeholder="28" unit="yrs"/>
              <Input label="Height" value={p.height_cm} onChange={v=>up("height_cm",v)} type="number" placeholder="170" unit="cm"/>
              <Input label="Weight" value={p.weight_kg} onChange={v=>up("weight_kg",v)} type="number" placeholder="75" unit="kg"/>
              <Input label="Goal Weight" value={p.goal_weight_kg} onChange={v=>up("goal_weight_kg",v)} type="number" placeholder="70" unit="kg"/>
            </div>

            {/* Sex */}
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
                color:C.muted,display:"block",marginBottom:8,fontFamily:"'Nunito Sans',sans-serif"}}>Sex</label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {[{v:"male",l:"Male",i:"♂"},{v:"female",l:"Female",i:"♀"},{v:"other",l:"Other",i:"◎"}].map(s=>{
                  const sel=p.sex===s.v;
                  return (
                    <button key={s.v} onClick={()=>{up("sex",s.v);up("cycle_phase","");}}
                      style={{padding:"12px 6px",borderRadius:12,border:"none",cursor:"pointer",
                        background:sel?`${C.grape}12`:C.card,
                        outline:`2px solid ${sel?C.grape:C.border}`,
                        boxShadow:sel?`2px 2px 0px ${C.ink}`:"none",transition:"all 0.18s"}}>
                      <div style={{fontSize:20,marginBottom:3}}>{s.i}</div>
                      <div style={{fontSize:13,fontWeight:800,color:sel?C.grape:C.ink}}>{s.l}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Hormonal phase */}
            {p.sex==="female"&&(
              <div style={{background:C.grapeDim,border:`1px solid ${C.grape}25`,
                borderRadius:14,padding:"14px 16px",animation:"fadeIn 0.3s ease both"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <span style={{fontSize:16}}>🌸</span>
                  <span style={{fontSize:11,fontWeight:800,color:C.grape,textTransform:"uppercase",
                    letterSpacing:"0.1em",fontFamily:"'Nunito Sans',sans-serif"}}>Hormonal Phase</span>
                </div>
                <Select value={p.cycle_phase} onChange={v=>up("cycle_phase",v)}
                  options={CYCLE_OPTS} placeholder="Select your status…"/>
              </div>
            )}

            {/* Body type */}
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
                color:C.muted,display:"block",marginBottom:8,fontFamily:"'Nunito Sans',sans-serif"}}>
                Body Type <span style={{fontWeight:600,fontSize:10,textTransform:"none"}}>(optional)</span>
              </label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {[{v:"ectomorph",l:"Ectomorph",s:"Lean, fast metabolism",c:C.sky},
                  {v:"mesomorph",l:"Mesomorph",s:"Athletic, balanced",c:C.mint},
                  {v:"endomorph",l:"Endomorph",s:"Rounded, carb-sensitive",c:C.coral},
                ].map(bt=>{
                  const sel=p.body_type===bt.v;
                  return (
                    <button key={bt.v} onClick={()=>up("body_type",sel?"":bt.v)}
                      style={{padding:"11px 6px",borderRadius:12,border:"none",cursor:"pointer",
                        background:sel?`${bt.c}12`:C.card,
                        outline:`2px solid ${sel?bt.c:C.border}`,
                        boxShadow:sel?`2px 2px 0px ${C.ink}`:"none",transition:"all 0.18s"}}>
                      <div style={{fontSize:12,fontWeight:800,color:sel?bt.c:C.ink,marginBottom:3}}>{bt.l}</div>
                      <div style={{fontSize:10,color:C.muted,lineHeight:1.3,fontWeight:600}}>{bt.s}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: LIFESTYLE ── */}
        {step===2&&(
          <div style={{display:"flex",flexDirection:"column",gap:16,animation:"fadeUp 0.3s ease both"}}>
            <Select label="Activity Level" value={p.activity_level}
              onChange={v=>up("activity_level",v)} options={ACT_OPTS} placeholder="Select level…"/>
            <Select label="Training History" value={p.training_history}
              onChange={v=>up("training_history",v)} options={HIST_OPTS} placeholder="Select experience…"/>

            {/* Days */}
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
                color:C.muted,display:"block",marginBottom:8,fontFamily:"'Nunito Sans',sans-serif"}}>Days Per Week</label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
                {[{d:"2",s:"Minimal"},{d:"3",s:"Balanced"},{d:"4",s:"Optimal"},{d:"5",s:"High"},{d:"6",s:"Elite"}].map(({d,s})=>{
                  const sel=p.training_days===d;
                  const rc=d==="3"||d==="4"?C.mint:d==="5"?C.amber:d==="6"?C.coral:C.sky;
                  return (
                    <button key={d} onClick={()=>up("training_days",d)}
                      style={{padding:"12px 4px",borderRadius:12,border:"none",textAlign:"center",cursor:"pointer",
                        background:sel?`${rc}15`:C.card,outline:`2px solid ${sel?rc:C.border}`,
                        boxShadow:sel?`2px 2px 0px ${C.ink}`:"none",transition:"all 0.18s"}}>
                      <div style={{fontSize:18,fontWeight:900,color:sel?rc:C.ink}}>{d}</div>
                      <div style={{fontSize:9,color:sel?rc:C.muted,fontWeight:700,marginTop:2,fontFamily:"'Nunito Sans',sans-serif"}}>{s}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Location */}
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
                color:C.muted,display:"block",marginBottom:8,fontFamily:"'Nunito Sans',sans-serif"}}>Location</label>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[{v:"gym",l:"Gym",i:"🏋️",s:"Full equipment"},{v:"home",l:"Home",i:"🏠",s:"Minimal / bodyweight"}].map(t=>{
                  const sel=p.training_type===t.v;
                  return (
                    <button key={t.v} onClick={()=>up("training_type",t.v)}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,
                        padding:"16px 10px",borderRadius:14,border:"none",cursor:"pointer",
                        background:sel?`${C.coral}12`:C.card,outline:`2px solid ${sel?C.coral:C.border}`,
                        boxShadow:sel?`2px 2px 0px ${C.ink}`:"none",transition:"all 0.18s"}}>
                      <span style={{fontSize:24}}>{t.i}</span>
                      <span style={{fontSize:13,fontWeight:800,color:sel?C.coral:C.ink}}>{t.l}</span>
                      <span style={{fontSize:10,color:C.muted,fontWeight:600,textAlign:"center"}}>{t.s}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Training style — split vs full body */}
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
                color:C.muted,display:"block",marginBottom:8,fontFamily:"'Nunito Sans',sans-serif"}}>Training Style</label>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[{v:"split",l:"Split",i:"🔀",s:"Push/Pull/Legs or Upper/Lower — muscles grouped by pattern"},
                  {v:"full_body",l:"Full Body",i:"💪",s:"All major muscles trained each session"}
                ].map(t=>{
                  const sel=p.training_style===t.v;
                  return (
                    <button key={t.v} onClick={()=>up("training_style",t.v)}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,
                        padding:"16px 10px",borderRadius:14,border:"none",cursor:"pointer",
                        background:sel?`${C.grape}12`:C.card,outline:`2px solid ${sel?C.grape:C.border}`,
                        boxShadow:sel?`2px 2px 0px ${C.ink}`:"none",transition:"all 0.18s"}}>
                      <span style={{fontSize:24}}>{t.i}</span>
                      <span style={{fontSize:13,fontWeight:800,color:sel?C.grape:C.ink}}>{t.l}</span>
                      <span style={{fontSize:10,color:C.muted,fontWeight:600,textAlign:"center",lineHeight:1.4}}>{t.s}</span>
                    </button>
                  );
                })}
              </div>
              {p.training_style&&(
                <div style={{marginTop:8,padding:"8px 12px",background:C.grapeDim,
                  border:`1px solid ${C.grape}20`,borderRadius:10,
                  fontSize:11,color:C.muted,lineHeight:1.5}}>
                  {p.training_style==="split"
                    ? "Research: 4-day Upper/Lower or 5-day PPL programmes — higher per-session volume per muscle group (ACSM)"
                    : "Research: 3-day Full Body — efficient minimum effective dose, suited for 2–3 training days (NSCA)"}
                </div>
              )}
            </div>

            {/* Injuries */}
            <div>
              <label style={{fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
                color:C.muted,display:"block",marginBottom:8,fontFamily:"'Nunito Sans',sans-serif"}}>
                Current Injuries / Pain <span style={{fontWeight:600,fontSize:10,textTransform:"none"}}>(optional)</span>
              </label>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[
                  {v:"none",       l:"None",              i:"✅", c:C.mint},
                  {v:"lower_back", l:"Lower Back",        i:"🔴", c:C.coral},
                  {v:"knee",       l:"Knee Pain",         i:"🔴", c:C.coral},
                  {v:"shoulder",   l:"Shoulder",          i:"🔴", c:C.amber},
                  {v:"wrist_elbow",l:"Wrist / Elbow",     i:"🔴", c:C.amber},
                  {v:"hip_flexor", l:"Hip Flexor",        i:"🔴", c:C.coral},
                ].map(inj=>{
                  const sel = p.injuries===inj.v;
                  return (
                    <button key={inj.v} onClick={()=>up("injuries",inj.v)}
                      style={{display:"flex",alignItems:"center",gap:8,padding:"11px 12px",
                        borderRadius:12,border:"none",cursor:"pointer",textAlign:"left",
                        background:sel?`${inj.c}12`:C.card,
                        outline:`2px solid ${sel?inj.c:C.border}`,
                        boxShadow:sel?`2px 2px 0px ${C.ink}`:"none",transition:"all 0.18s"}}>
                      <span style={{fontSize:16}}>{inj.i}</span>
                      <span style={{fontSize:12,fontWeight:800,color:sel?inj.c:C.ink}}>{inj.l}</span>
                    </button>
                  );
                })}
              </div>
              {p.injuries&&p.injuries!=="none"&&(
                <div style={{marginTop:8,padding:"10px 12px",background:C.amberDim||"rgba(245,151,0,0.10)",
                  border:`1px solid ${C.amber}30`,borderRadius:10,fontSize:11,color:C.muted,lineHeight:1.6}}>
                  ⚠️ Exercises that aggravate <strong style={{color:C.amber}}>{
                    {lower_back:"lower back pain",knee:"knee pain",shoulder:"shoulder impingement",
                     wrist_elbow:"wrist/elbow pain",hip_flexor:"hip flexor strain"}[p.injuries]
                  }</strong> will be automatically swapped for safer alternatives in your programme.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 3: DIET ── */}
        {step===3&&(
          <div style={{display:"flex",flexDirection:"column",gap:14,animation:"fadeUp 0.3s ease both"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[{v:"auto",l:"Auto-Calculate",i:"🧮",s:"App sets macros from your data",c:C.mint},
                {v:"manual",l:"Custom Macros",i:"✏️",s:"Enter your own targets",c:C.coral},
              ].map(m=>{
                const sel=p.macro_mode===m.v;
                return (
                  <button key={m.v} onClick={()=>up("macro_mode",m.v)}
                    style={{display:"flex",flexDirection:"column",gap:8,padding:"18px 14px",
                      borderRadius:16,border:"none",textAlign:"left",cursor:"pointer",
                      background:sel?`${m.c}12`:C.card,
                      outline:`2.5px solid ${sel?m.c:C.border}`,
                      boxShadow:sel?`3px 3px 0px ${C.ink}`:"2px 2px 0px rgba(26,26,46,0.05)",
                      transition:"all 0.18s"}}>
                    <span style={{fontSize:26}}>{m.i}</span>
                    <span style={{fontSize:14,fontWeight:800,color:sel?m.c:C.ink}}>{m.l}</span>
                    <span style={{fontSize:11,color:C.muted,lineHeight:1.5,fontWeight:600}}>{m.s}</span>
                  </button>
                );
              })}
            </div>

            {p.macro_mode==="auto"&&(
              <Card style={{background:C.mintDim,border:`1px solid ${C.mint}30`}}>
                <div style={{fontSize:12,fontWeight:800,color:"#00A878",marginBottom:6}}>✓ GAINR calculates your macros</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.7}}>
                  Based on your goal, biometrics, and activity level. Grounded in ACSM, ISSN, and WHO guidelines.
                </div>
              </Card>
            )}

            {p.macro_mode==="manual"&&(
              <div style={{display:"flex",flexDirection:"column",gap:12,animation:"fadeUp 0.3s ease both"}}>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.6,padding:"10px 14px",
                  background:C.coralDim,borderRadius:10,border:`1px solid ${C.coral}20`}}>
                  Leave any blank and GAINR will calculate it from your profile.
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <Input label="Calories" value={p.custom_calories} onChange={v=>up("custom_calories",v)} type="number" placeholder="2200" unit="kcal"/>
                  <Input label="Protein"  value={p.custom_protein}  onChange={v=>up("custom_protein",v)}  type="number" placeholder="160"  unit="g"/>
                  <Input label="Carbs"    value={p.custom_carbs}    onChange={v=>up("custom_carbs",v)}    type="number" placeholder="220"  unit="g"/>
                  <Input label="Fats"     value={p.custom_fats}     onChange={v=>up("custom_fats",v)}     type="number" placeholder="70"   unit="g"/>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,
        background:"rgba(255,248,240,0.97)",backdropFilter:"blur(20px)",
        borderTop:`2px solid ${C.border}`,padding:"14px 20px 32px"}}>
        <div style={{maxWidth:500,margin:"0 auto"}}>
          {!canNext()&&(
            <div style={{marginBottom:10,padding:"8px 12px",background:C.coralDim,
              border:`1px solid ${C.coral}30`,borderRadius:10,fontSize:12,color:C.coral,lineHeight:1.5}}>
              {step===0&&"Select a goal to continue"}
              {step===1&&"Fill in your age, height, weight and sex"}
              {step===2&&"Select activity level, training experience and location"}
            </div>
          )}
          <div style={{display:"flex",gap:10}}>
            {step>0&&(
              <Btn variant="ghost" onClick={()=>setStep(s=>s-1)}>← Back</Btn>
            )}
            <Btn onClick={()=>{
              if(!canNext()) return;
              if(step<OB_STEPS.length-1) setStep(s=>s+1);
              else handleFinish();
            }} disabled={!canNext()} style={{flex:1}}>
              {step===OB_STEPS.length-1?"Build My Plan →":"Next →"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREEN: PLAN — macros + workout programme
// ═══════════════════════════════════════════════════════════════
function PlanScreen({profile,macros,sessions,weekNum,onWorkout}) {
  const block = getCurrentBlock(weekNum);
  const BLOCK_META = [
    {name:"Foundation", icon:"🏗", weeks:"Wks 1–3",  range:[1,3],  color:C.sky},
    {name:"Hypertrophy",icon:"💪", weeks:"Wks 4–7",  range:[4,7],  color:C.grape},
    {name:"Strength",   icon:"⚡", weeks:"Wks 8–10", range:[8,10], color:C.coral},
    {name:"Deload",     icon:"🌙", weeks:"Wk 11",    range:[11,11],color:C.sunshine},
  ];
  const cyclePos=((weekNum-1)%11)+1;
  const curIdx=BLOCK_META.findIndex(b=>cyclePos>=b.range[0]&&cyclePos<=b.range[1]);
  const weekInBlock=cyclePos-BLOCK_META[curIdx||0].range[0]+1;
  const blockLen=BLOCK_META[curIdx||0].range[1]-BLOCK_META[curIdx||0].range[0]+1;
  const blockPct=Math.round((weekInBlock/blockLen)*100);

  const RPE_INFO={
    Foundation: {rpe:"6–7",filled:6,sets:"8–10 sets/muscle/wk",next:"Hypertrophy block — volume increases, RPE rises to 7–8."},
    Hypertrophy:{rpe:"7–8",filled:8,sets:"10–16 sets/muscle/wk",next:"Strength block — load increases, volume drops. RPE 8–9."},
    Strength:   {rpe:"8–9",filled:9,sets:"6–10 sets/muscle/wk",next:"Deload week — reduce volume 30–50%, keep load moderate."},
    Deload:     {rpe:"4–5",filled:5,sets:"50% normal volume",next:"New cycle begins — Foundation block. Fresh start."},
  };
  const rpe=RPE_INFO[block.name]||RPE_INFO.Foundation;

  const goalMeta={
    cut:{label:"Fat Loss",color:C.coral},gain:{label:"Muscle Gain",color:C.sky},
    maintain:{label:"Maintenance",color:C.mint},recomp:{label:"Recomposition",color:C.grape},
  }[profile.goal]||{label:"Training",color:C.grape};

  return (
    <div style={{animation:"fadeUp 0.4s ease both"}}>

      {/* ── HERO MACRO CARD ── */}
      <div style={{background:C.ink,borderRadius:20,padding:"22px 20px",marginBottom:14,
        boxShadow:`5px 5px 0px ${C.coral}`}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",
              color:"rgba(255,255,255,0.4)",marginBottom:4,fontFamily:"'Nunito Sans',sans-serif"}}>
              Your Plan · Week {weekNum}
            </div>
            <Pill label={goalMeta.label} color={goalMeta.color}/>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontFamily:"'Righteous',cursive",fontSize:34,color:C.sunshine,lineHeight:1}}>{macros.calories}</div>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.4)",
              fontFamily:"'Nunito Sans',sans-serif",letterSpacing:"0.08em",textTransform:"uppercase"}}>kcal / day</div>
          </div>
        </div>

        {/* Macro pills */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
          {[
            {v:macros.proteinG,l:"Protein",u:"g",c:C.mint},
            {v:macros.carbG,   l:"Carbs",  u:"g",c:C.sunshine},
            {v:macros.fatG,    l:"Fats",   u:"g",c:C.sky},
          ].map(m=>(
            <div key={m.l} style={{background:"rgba(255,255,255,0.07)",borderRadius:12,
              padding:"12px 10px",border:"1px solid rgba(255,255,255,0.09)"}}>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:22,color:m.c,lineHeight:1}}>{m.v}{m.u}</div>
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.4)",marginTop:3,
                textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"'Nunito Sans',sans-serif"}}>{m.l}</div>
            </div>
          ))}
        </div>

        {/* Deficit/surplus context */}
        {(macros.deficit||macros.surplus)&&(
          <div style={{marginTop:12,fontSize:11,color:"rgba(255,255,255,0.35)",
            fontFamily:"'Nunito Sans',sans-serif",lineHeight:1.5}}>
            {macros.deficit&&`📉 ${macros.deficit} kcal/day deficit · ACSM recommended rate`}
            {macros.surplus&&`📈 ${macros.surplus} kcal/day surplus · Conservative bulk (Helms et al.)`}
          </div>
        )}
      </div>

      {/* ── MEAL DISTRIBUTION ── */}
      <Card style={{marginBottom:14}}>
        <SLabel>Protein Distribution</SLabel>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{flex:1,fontSize:13,color:C.muted,lineHeight:1.7}}>
            Aim for <strong style={{color:C.ink}}>{macros.proteinPerMeal}g protein</strong> across{" "}
            <strong style={{color:C.ink}}>4 meals</strong> per day.
          </div>
          <div style={{flexShrink:0,textAlign:"center",padding:"8px 14px",
            background:C.mintDim,borderRadius:10,border:`1px solid ${C.mint}30`}}>
            <div style={{fontFamily:"'Righteous',cursive",fontSize:22,color:"#00A878"}}>{macros.proteinPerMeal}g</div>
            <div style={{fontSize:9,fontWeight:700,color:"#00A878",textTransform:"uppercase",
              letterSpacing:"0.08em",fontFamily:"'Nunito Sans',sans-serif"}}>per meal</div>
          </div>
        </div>
      </Card>

      {/* ── PERIODISATION BLOCK TRACKER ── */}
      <Card style={{marginBottom:14}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",
          color:C.muted,marginBottom:10,fontFamily:"'Nunito Sans',sans-serif"}}>
          Training Cycle · Week {weekNum}
        </div>

        {/* 4 tiles */}
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          {BLOCK_META.map((b,i)=>{
            const isDone=i<curIdx, isCur=i===curIdx, isDeload=b.name==="Deload";
            return (
              <div key={b.name} style={{flex:1,borderRadius:12,padding:"11px 6px 10px",
                textAlign:"center",position:"relative",
                background:isDone?`${C.mint}10`:isCur?C.card:isDeload?`rgba(255,210,63,0.12)`:C.faint,
                border:isCur?`2px solid ${C.grape}`:isDone?`1.5px solid ${C.mint}40`:
                  isDeload?`1.5px solid rgba(255,210,63,0.4)`:`1.5px solid ${C.border}`,
                boxShadow:isCur?`3px 3px 0px ${C.ink}`:"none",transition:"all 0.2s"}}>
                {isCur&&(
                  <div style={{position:"absolute",top:-9,left:"50%",transform:"translateX(-50%)",
                    fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:99,whiteSpace:"nowrap",
                    letterSpacing:"0.05em",textTransform:"uppercase",background:C.grape,color:"#fff",
                    fontFamily:"'Nunito Sans',sans-serif"}}>Now</div>
                )}
                <div style={{fontSize:15,marginBottom:4,opacity:!isDone&&!isCur&&!isDeload?0.3:1}}>{b.icon}</div>
                <div style={{fontSize:10,fontWeight:800,fontFamily:"'Nunito Sans',sans-serif",
                  color:isDone?"#00C896":isCur?C.grape:isDeload?"#B8860B":C.muted}}>{b.name}</div>
                <div style={{fontSize:9,color:isCur?C.grape:C.muted,marginTop:1,
                  fontFamily:"'Nunito Sans',sans-serif"}}>{b.weeks}</div>
              </div>
            );
          })}
        </div>

        {/* Detail */}
        <div style={{background:C.faint,borderRadius:12,padding:"14px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:17,color:C.ink,marginBottom:2}}>{block.name} block</div>
              <div style={{fontSize:12,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontWeight:600}}>
                {block.focus} · Week {weekInBlock} of {blockLen}
              </div>
            </div>
            <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:99,
              background:`${C.grape}12`,color:C.grape,border:`1px solid ${C.grape}25`,
              fontFamily:"'Nunito Sans',sans-serif"}}>{blockPct}% done</span>
          </div>
          <div style={{height:5,borderRadius:99,background:"rgba(26,26,46,0.08)",overflow:"hidden",marginBottom:12}}>
            <div style={{height:"100%",borderRadius:99,
              background:`linear-gradient(90deg,${C.grape},${C.coral})`,width:`${blockPct}%`}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
            <div style={{background:C.card,borderRadius:10,padding:"10px 12px",border:`1px solid ${C.border}`}}>
              <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",
                letterSpacing:"0.08em",marginBottom:4,fontFamily:"'Nunito Sans',sans-serif"}}>Target RPE</div>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:16,color:C.coral,marginBottom:4}}>{rpe.rpe}</div>
              <div style={{display:"flex",gap:3}}>
                {[1,2,3,4,5,6,7,8,9,10].map(n=>(
                  <div key={n} style={{height:4,flex:1,borderRadius:99,
                    background:n<=rpe.filled?C.mint:C.faint}}/>
                ))}
              </div>
            </div>
            <div style={{background:C.card,borderRadius:10,padding:"10px 12px",border:`1px solid ${C.border}`}}>
              <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",
                letterSpacing:"0.08em",marginBottom:4,fontFamily:"'Nunito Sans',sans-serif"}}>Focus</div>
              <div style={{fontSize:12,fontWeight:800,color:C.ink,lineHeight:1.35}}>{block.focus}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:3,fontFamily:"'Nunito Sans',sans-serif"}}>{rpe.sets}</div>
            </div>
          </div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.55,fontFamily:"'Nunito Sans',sans-serif",
            paddingTop:10,borderTop:`1px solid ${C.border}`}}>
            <span style={{color:C.coral,fontWeight:800}}>Next → </span>{rpe.next}
          </div>
        </div>
      </Card>

      {/* ── WEEKLY SCHEDULE ── */}
      <Card style={{marginBottom:14}}>
        <SLabel>This Week's Sessions</SLabel>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {sessions.map((s,i)=>{
            const isCardio=s.type==="Cardio";
            const col=isCardio?C.sky:C.coral;
            return (
              <button key={i} onClick={()=>!isCardio&&onWorkout(s)}
                style={{display:"flex",alignItems:"center",gap:12,padding:"14px",
                  borderRadius:13,border:`1.5px solid ${col}25`,
                  background:`${col}08`,cursor:isCardio?"default":"pointer",
                  textAlign:"left",width:"100%",transition:"all 0.18s"}}>
                <div style={{width:40,height:40,borderRadius:10,flexShrink:0,
                  background:col,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:18,boxShadow:`2px 2px 0px ${C.ink}`}}>
                  {isCardio?"🚶":"🏋️"}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:800,fontSize:14,color:C.ink,marginBottom:2}}>{s.day} — {s.label}</div>
                  <div style={{fontSize:12,color:col,fontWeight:700,marginBottom:s.exercises?.length?2:0}}>{s.focus}</div>
                  {!isCardio&&s.exercises?.length>0&&(
                    <div style={{fontSize:11,color:C.muted,fontFamily:"'Nunito Sans',sans-serif"}}>
                      {s.exercises.length} exercises · tap to open
                    </div>
                  )}
                </div>
                {!isCardio&&<span style={{fontSize:20,color:C.muted,flexShrink:0}}>›</span>}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Evidence footnote */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:32}}>
        {["ACSM","ISSN","WHO","NICE"].map(t=><Pill key={t} label={t} color={C.grape}/>)}
        <div style={{width:"100%",fontSize:11,color:C.muted,marginTop:6,lineHeight:1.6,
          fontFamily:"'Nunito Sans',sans-serif"}}>
          Your plan is evidence-based and adapts weekly. {block.name} block — RPE {block.rpe} target.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREEN: WORKOUT — per-set logging with REST timer
// Matches sketch: exercise card, set rows, REST button, kg input, complete circle
// ═══════════════════════════════════════════════════════════════
function FooterRestTimer({seconds, exerciseName, setNum, onClose}) {
  const [remaining, setRemaining] = React.useState(seconds);
  const [paused,    setPaused]    = React.useState(false);
  const done = remaining <= 0;

  React.useEffect(()=>{
    if (paused || done) return;
    const t = setTimeout(()=>setRemaining(r=>r-1), 1000);
    return ()=>clearTimeout(t);
  },[remaining, paused, done]);

  const pct  = Math.max(0, (remaining/seconds)*100);
  const mins  = Math.floor(remaining/60);
  const secs  = remaining%60;

  return (
    <div style={{position:"fixed",bottom:82,left:0,right:0,zIndex:200,
      animation:"slideUp 0.3s ease both"}}>
      <div style={{margin:"0 12px",background:done?C.mint:C.ink,borderRadius:16,
        padding:"14px 16px",boxShadow:`0 -4px 24px rgba(26,26,46,0.25), 0 0 0 2px ${done?C.mint:C.coral}`,
        border:`2px solid ${done?C.mint:C.coral}`}}>
        {/* Progress bar */}
        <div style={{height:4,borderRadius:99,background:"rgba(255,255,255,0.15)",
          overflow:"hidden",marginBottom:12}}>
          <div style={{height:"100%",borderRadius:99,
            background:done?C.ink:C.coral,
            width:`${pct}%`,transition:"width 0.9s linear"}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
          {/* Label */}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:10,fontWeight:700,color:done?C.ink:"rgba(255,255,255,0.5)",
              textTransform:"uppercase",letterSpacing:"0.08em",
              fontFamily:"'Nunito Sans',sans-serif",marginBottom:2}}>
              {done?"Ready for next set!":"Rest"}
            </div>
            <div style={{fontSize:12,fontWeight:700,color:done?C.ink:"rgba(255,255,255,0.7)",
              fontFamily:"'Nunito Sans',sans-serif",whiteSpace:"nowrap",
              overflow:"hidden",textOverflow:"ellipsis"}}>
              {exerciseName} · Set {setNum}
            </div>
          </div>
          {/* Timer display */}
          {!done&&(
            <div style={{fontFamily:"'Righteous',cursive",fontSize:28,
              color:"#fff",lineHeight:1,flexShrink:0}}>
              {mins>0?`${mins}:${secs.toString().padStart(2,"0")}`:secs}
            </div>
          )}
          {/* Controls */}
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            {!done&&<>
              <button onClick={()=>setRemaining(r=>Math.max(5,r-15))}
                style={{padding:"6px 10px",borderRadius:8,border:"none",
                  background:"rgba(255,255,255,0.12)",color:"#fff",
                  fontSize:11,fontWeight:800,cursor:"pointer",
                  fontFamily:"'Nunito Sans',sans-serif"}}>-15s</button>
              <button onClick={()=>setRemaining(r=>r+15)}
                style={{padding:"6px 10px",borderRadius:8,border:"none",
                  background:"rgba(255,255,255,0.12)",color:"#fff",
                  fontSize:11,fontWeight:800,cursor:"pointer",
                  fontFamily:"'Nunito Sans',sans-serif"}}>+15s</button>
              <button onClick={()=>setPaused(v=>!v)}
                style={{padding:"6px 10px",borderRadius:8,border:"none",
                  background:"rgba(255,255,255,0.12)",color:"#fff",
                  fontSize:14,cursor:"pointer"}}>
                {paused?"▶":"⏸"}
              </button>
            </>}
            <button onClick={onClose}
              style={{padding:"6px 12px",borderRadius:8,border:"none",
                background:done?C.ink:C.coral,color:"#fff",
                fontSize:11,fontWeight:800,cursor:"pointer",
                fontFamily:"'Nunito Sans',sans-serif"}}>
              {done?"Go! 💪":"Skip"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// EXERCISE CARD — per-set rows with kg + complete circle
// ═══════════════════════════════════════════════════════════════
function ExerciseCard({ex, exIndex, onUpdate, onRestStart}) {
  const setCount   = typeof ex.sets==="number" ? ex.sets : parseInt(ex.sets)||3;
  const defaultReps = (()=>{
    if (!ex.reps) return 8;
    const m = String(ex.reps).match(/(\d+)/g);
    return m ? parseInt(m[m.length-1]) : 8;
  })();
  const restSecs = (()=>{
    if (!ex.rest) return 90;
    const m = String(ex.rest).match(/(\d+)[^0-9]+(\d+)/);
    if (m) return Math.round((parseInt(m[1])+parseInt(m[2]))/2)*60;
    const m2 = String(ex.rest).match(/(\d+)/);
    return m2 ? parseInt(m2[1])*60 : 90;
  })();

  const [sets, setSets] = React.useState(
    ()=>Array.from({length:setCount},()=>({reps:defaultReps,kg:"",done:false}))
  );
  const allDone = sets.every(s=>s.done);

  function updateSet(i,field,val) {
    setSets(prev=>{
      const next = prev.map((s,idx)=>idx===i?{...s,[field]:val}:s);
      onUpdate && onUpdate(next.map(s=>({...s,targetReps:defaultReps})));
      return next;
    });
  }

  function completeSet(i) {
    setSets(prev=>{
      const next = prev.map((s,idx)=>idx===i?{...s,done:!s.done}:s);
      // Trigger rest timer when marking done (not undoing)
      if (!prev[i].done && prev[i].kg) {
        onRestStart && onRestStart(restSecs, ex.name, i+1);
      }
      onUpdate && onUpdate(next.map(s=>({...s,targetReps:defaultReps})));
      return next;
    });
  }

  return (
    <div style={{background:allDone?`${C.mint}08`:C.card,
      border:`1.5px solid ${allDone?C.mint:C.border}`,borderRadius:16,
      overflow:"hidden",marginBottom:12,transition:"all 0.25s"}}>

      {/* Header */}
      <div style={{padding:"14px 16px 10px",borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:"flex",alignItems:"flex-start",
          justifyContent:"space-between",marginBottom:6}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:2}}>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:17,
                color:allDone?C.muted:C.ink,textDecoration:allDone?"line-through":"none"}}>
                {ex.name}
              </div>
              {ex.injuryMod&&(
                <span style={{fontSize:9,fontWeight:800,color:C.amber,
                  background:`${C.amber}15`,border:`1px solid ${C.amber}30`,
                  borderRadius:20,padding:"1px 7px",fontFamily:"'Nunito Sans',sans-serif"}}>
                  ⚠️ Modified
                </span>
              )}
            </div>
            <div style={{fontSize:12,fontWeight:700,color:C.muted,
              fontFamily:"'Nunito Sans',sans-serif"}}>{ex.muscle}</div>
          </div>
          {/* REST button — triggers footer timer */}
          <button onClick={()=>onRestStart&&onRestStart(restSecs,ex.name,"—")}
            style={{flexShrink:0,marginLeft:10,padding:"7px 12px",borderRadius:10,
              border:`1.5px solid ${C.coral}35`,background:C.coralDim,
              color:C.coral,fontWeight:800,fontSize:11,cursor:"pointer",
              fontFamily:"'Nunito Sans',sans-serif",display:"flex",
              alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
            ⏱ REST: {restSecs>=60?`${Math.round(restSecs/60)}min`:`${restSecs}s`}
          </button>
        </div>
        <div style={{display:"flex",gap:6}}>
          <span style={{fontSize:11,fontWeight:700,background:`${C.grape}10`,color:C.grape,
            border:`1px solid ${C.grape}20`,borderRadius:20,padding:"2px 8px",
            fontFamily:"'Nunito Sans',sans-serif"}}>{setCount} sets × {ex.reps} reps</span>
          <span style={{fontSize:11,fontWeight:700,background:`${C.ink}08`,color:C.muted,
            border:`1px solid ${C.border}`,borderRadius:20,padding:"2px 8px",
            fontFamily:"'Nunito Sans',sans-serif"}}>RPE {ex.rpe}</span>
        </div>
      </div>

      {/* Column headers */}
      <div style={{display:"grid",gridTemplateColumns:"40px 1fr 1fr 40px",
        padding:"7px 16px 5px",background:C.faint,borderBottom:`1px solid ${C.border}`}}>
        {["SET","REPS","KG","✓"].map(h=>(
          <div key={h} style={{fontSize:10,fontWeight:800,color:C.muted,textAlign:"center",
            fontFamily:"'Nunito Sans',sans-serif",letterSpacing:"0.08em",
            textTransform:"uppercase"}}>{h}</div>
        ))}
      </div>

      {/* Set rows */}
      <div style={{padding:"4px 12px 10px"}}>
        {sets.map((s,i)=>(
          <div key={i} style={{display:"grid",gridTemplateColumns:"40px 1fr 1fr 40px",
            alignItems:"center",padding:"7px 4px",
            borderBottom:i<sets.length-1?`1px solid ${C.border}`:"none"}}>
            {/* Set number */}
            <div style={{textAlign:"center",fontFamily:"'Righteous',cursive",
              fontSize:15,color:s.done?C.mint:C.muted}}>{i+1}</div>
            {/* Reps — prefilled, editable */}
            <div style={{padding:"0 5px"}}>
              <input type="number" value={s.reps}
                onChange={e=>updateSet(i,"reps",parseInt(e.target.value)||0)}
                style={{width:"100%",padding:"8px 4px",borderRadius:8,textAlign:"center",
                  border:`1.5px solid ${s.done?C.mint:C.border}`,
                  background:s.done?`${C.mint}08`:C.bg,
                  fontSize:14,fontWeight:800,color:s.done?C.mint:C.ink,
                  outline:"none",fontFamily:"'Nunito',sans-serif"}}/>
            </div>
            {/* KG input */}
            <div style={{padding:"0 5px"}}>
              <input type="number" placeholder="kg" value={s.kg}
                onChange={e=>updateSet(i,"kg",e.target.value)}
                style={{width:"100%",padding:"8px 4px",borderRadius:8,textAlign:"center",
                  border:`1.5px solid ${s.kg?(s.done?C.mint:C.coral):C.border}`,
                  background:s.done?`${C.mint}08`:C.bg,
                  fontSize:14,fontWeight:800,
                  color:s.done?C.mint:s.kg?C.ink:C.muted,
                  outline:"none",fontFamily:"'Nunito',sans-serif"}}/>
            </div>
            {/* Complete circle */}
            <div style={{display:"flex",justifyContent:"center"}}>
              <button onClick={()=>completeSet(i)}
                style={{width:30,height:30,borderRadius:"50%",border:"none",
                  cursor:"pointer",
                  background:s.done?C.mint:s.kg?"rgba(61,255,192,0.2)":"transparent",
                  outline:`2px solid ${s.done?C.mint:s.kg?C.mint:C.border}`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  transition:"all 0.2s",
                  boxShadow:s.done?`1px 1px 0px ${C.ink}`:"none"}}>
                {s.done&&<span style={{fontSize:13,color:C.ink,fontWeight:900}}>✓</span>}
              </button>
            </div>
          </div>
        ))}
      </div>

      {ex.note&&(
        <div style={{padding:"0 16px 12px",fontSize:11,color:C.muted,
          lineHeight:1.5,fontFamily:"'Nunito Sans',sans-serif"}}>💡 {ex.note}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREEN: WORKOUT — full rebuild with Finish button + footer REST timer
// ═══════════════════════════════════════════════════════════════
function WorkoutScreen({session, userId, sessionId, onBack}) {
  const [startTime]    = React.useState(()=>new Date());
  const [finished,     setFinished]     = React.useState(false);
  const [saving,       setSaving]       = React.useState(false);
  const [exerciseData, setExerciseData] = React.useState({});
  // Footer rest timer state
  const [restTimer,    setRestTimer]    = React.useState(null);
  // {seconds, exerciseName, setNum}

  const exercises = session?.exercises || [];

  function handleExUpdate(idx, sets) {
    setExerciseData(prev=>({...prev,[idx]:sets}));
  }
  function handleRestStart(secs, name, setNum) {
    setRestTimer({seconds:secs, exerciseName:name, setNum});
  }

  const allSets       = Object.values(exerciseData).flat();
  const completedSets = allSets.filter(s=>s.done).length;
  const totalSets     = allSets.length;
  const overallPct    = totalSets>0 ? Math.round((completedSets/totalSets)*100) : 0;

  async function handleFinish() {
    setSaving(true);
    try {
      if (userId && sessionId) {
        const durationMinutes = Math.round((new Date()-startTime)/60000);
        await finishWorkoutSession(sessionId, {
          durationMinutes, completedSets, totalSets,
        });
        for (let i=0; i<exercises.length; i++) {
          const ex   = exercises[i];
          const sets = exerciseData[i]||[];
          if (sets.length>0) {
            await logExerciseSets(sessionId, userId, ex.name, ex.muscle, ex.type,
              sets.map(s=>({targetReps:s.targetReps,actualReps:s.reps,
                kg:s.kg,done:s.done})));
          }
        }
      }
    } catch(e) { console.warn("Finish session save failed:", e); }
    setSaving(false);
    setFinished(true);
  }

  if (finished) return (
    <div style={{textAlign:"center",padding:"60px 20px",animation:"popIn 0.3s ease both"}}>
      <div style={{fontSize:56,marginBottom:16}}>🎉</div>
      <div style={{fontFamily:"'Righteous',cursive",fontSize:28,color:C.ink,marginBottom:8}}>
        Session Complete!
      </div>
      <div style={{fontSize:14,color:C.muted,lineHeight:1.7,marginBottom:16}}>
        {completedSets} of {totalSets} sets logged · {overallPct}% completion
      </div>
      <div style={{height:8,borderRadius:99,background:C.faint,overflow:"hidden",
        maxWidth:200,margin:"0 auto 24px"}}>
        <div style={{height:"100%",background:`linear-gradient(90deg,${C.coral},${C.mint})`,
          width:`${overallPct}%`,borderRadius:99}}/>
      </div>
      <Btn onClick={onBack} fullWidth>← Back to Plan</Btn>
    </div>
  );

  return (
    <div style={{animation:"fadeUp 0.4s ease both",
      paddingBottom:restTimer?100:16}}>
      {/* Footer rest timer */}
      {restTimer&&(
        <FooterRestTimer
          seconds={restTimer.seconds}
          exerciseName={restTimer.exerciseName}
          setNum={restTimer.setNum}
          onClose={()=>setRestTimer(null)}/>
      )}

      <button onClick={onBack}
        style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",
          color:C.muted,fontWeight:700,fontSize:13,cursor:"pointer",
          marginBottom:16,padding:0}}>← Back</button>

      <div style={{marginBottom:6}}>
        <div style={{fontFamily:"'Righteous',cursive",fontSize:26,color:C.ink,
          lineHeight:1,marginBottom:4}}>{session?.label}</div>
        <div style={{fontSize:13,color:C.coral,fontWeight:700,
          marginBottom:8}}>{session?.focus}</div>
      </div>

      {session?.note&&(
        <div style={{background:C.faint,borderRadius:10,padding:"10px 14px",
          fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:16}}>
          💡 {session.note}
        </div>
      )}

      {/* Overall progress bar */}
      {totalSets>0&&(
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:11,fontWeight:700,color:C.muted,
              fontFamily:"'Nunito Sans',sans-serif",textTransform:"uppercase",
              letterSpacing:"0.08em"}}>Session Progress</span>
            <span style={{fontSize:11,fontWeight:800,color:C.coral,
              fontFamily:"'Nunito Sans',sans-serif"}}>{completedSets}/{totalSets} sets</span>
          </div>
          <div style={{height:5,borderRadius:99,background:C.faint,overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:99,
              background:`linear-gradient(90deg,${C.coral},${C.grape})`,
              width:`${overallPct}%`,transition:"width 0.3s ease"}}/>
          </div>
        </div>
      )}

      {exercises.length===0&&(
        <div style={{background:C.coralDim,border:`1px solid ${C.coral}30`,
          borderRadius:12,padding:"16px",marginBottom:16,
          fontSize:13,color:C.muted,lineHeight:1.7}}>
          <strong style={{color:C.coral}}>No exercises found.</strong> Go to Settings → Start Over to regenerate your plan.
        </div>
      )}

      <div style={{marginBottom:16}}>
        {exercises.map((ex,i)=>(
          <ExerciseCard key={`${session?.label}-${i}`} ex={ex} exIndex={i}
            onUpdate={sets=>handleExUpdate(i,sets)}
            onRestStart={handleRestStart}/>
        ))}
      </div>

      {/* Finish Session button */}
      <div style={{background:C.faint,borderRadius:14,padding:"16px",marginBottom:8}}>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:12,
          fontFamily:"'Nunito Sans',sans-serif",textAlign:"center"}}>
          {completedSets===totalSets&&totalSets>0
            ? "✅ All sets done — great work!"
            : totalSets>0 ? `${totalSets-completedSets} sets remaining` : "Complete your sets above"}
        </div>
        <Btn onClick={handleFinish} loading={saving} fullWidth
          variant={completedSets>0?"secondary":"ghost"}>
          {completedSets===totalSets&&totalSets>0
            ? "🏆 Finish & Save Session"
            : "Finish Session →"}
        </Btn>
      </div>

      <Btn onClick={onBack} fullWidth variant="ghost">← Back to Plan</Btn>
    </div>
  );
}

function ProgressScreen({onNav}) {
  // Sample weight entries stored in localStorage for persistence across sessions
  const [weights, setWeights] = React.useState(()=>{
    try { return JSON.parse(localStorage.getItem("gainr_weights")||"[]"); } catch{ return []; }
  });
  const [photos, setPhotos] = React.useState(()=>{
    try { return JSON.parse(localStorage.getItem("gainr_photos")||"[]"); } catch{ return []; }
  });

  const lastWeight = weights.length ? weights[weights.length-1] : null;
  const lastPhoto  = photos.length  ? photos[photos.length-1]  : null;

  return (
    <div style={{animation:"fadeUp 0.4s ease both"}}>
      <h1 style={{fontFamily:"'Righteous',cursive",fontSize:30,color:C.ink,marginBottom:4}}>My Progress</h1>
      <p style={{fontSize:13,color:C.muted,marginBottom:24,lineHeight:1.6}}>Track your body weight and physique changes over time.</p>

      {/* Body Weight card */}
      <button onClick={()=>onNav("bodyweight")}
        style={{width:"100%",background:C.card,border:`1.5px solid ${C.border}`,
          borderRadius:18,padding:"20px",marginBottom:14,textAlign:"left",cursor:"pointer",
          boxShadow:"3px 3px 0px rgba(26,26,46,0.08)",transition:"all 0.18s"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:42,height:42,borderRadius:12,background:C.skyDim,
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,
              border:`1px solid ${C.sky}30`}}>⚖️</div>
            <div>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:18,color:C.ink}}>Body Weight</div>
              <div style={{fontSize:12,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontWeight:600}}>
                {weights.length ? `${weights.length} entries logged` : "No entries yet"}
              </div>
            </div>
          </div>
          {lastWeight&&(
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:24,color:C.sky}}>{lastWeight.kg}kg</div>
              <div style={{fontSize:10,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontWeight:700}}>latest</div>
            </div>
          )}
        </div>
        {/* Mini sparkline */}
        {weights.length >= 2 ? (
          <svg width="100%" height="36" style={{display:"block"}}>
            {(()=>{
              const vals = weights.slice(-14).map(w=>w.kg);
              const min = Math.min(...vals), max = Math.max(...vals), range = max-min||1;
              const pts = vals.map((v,i)=>{
                const x = (i/(vals.length-1))*100+"%";
                const y = 30 - ((v-min)/range)*26;
                return `${i===0?"M":"L"} ${(i/(vals.length-1))*320} ${y}`;
              }).join(" ");
              return <path d={pts} fill="none" stroke={C.sky} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>;
            })()}
          </svg>
        ) : (
          <div style={{height:36,display:"flex",alignItems:"center",
            fontSize:12,color:C.muted,fontFamily:"'Nunito Sans',sans-serif"}}>
            Log your weight daily to see your trend →
          </div>
        )}
        <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
          <span style={{fontSize:12,fontWeight:800,color:C.sky,fontFamily:"'Nunito Sans',sans-serif"}}>View details ›</span>
        </div>
      </button>

      {/* Photos card */}
      <button onClick={()=>onNav("photos")}
        style={{width:"100%",background:C.card,border:`1.5px solid ${C.border}`,
          borderRadius:18,padding:"20px",textAlign:"left",cursor:"pointer",
          boxShadow:"3px 3px 0px rgba(26,26,46,0.08)",transition:"all 0.18s"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:42,height:42,borderRadius:12,background:C.grapeDim,
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,
              border:`1px solid ${C.grape}30`}}>📸</div>
            <div>
              <div style={{fontFamily:"'Righteous',cursive",fontSize:18,color:C.ink}}>Progress Photos</div>
              <div style={{fontSize:12,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontWeight:600}}>
                {photos.length ? `${Math.ceil(photos.length/3)} check-ins · Front, Side, Back` : "No photos yet"}
              </div>
            </div>
          </div>
          <span style={{fontSize:20,color:C.muted}}>›</span>
        </div>
        {/* Photo thumbnails preview */}
        {photos.length > 0 ? (
          <div style={{display:"flex",gap:8}}>
            {photos.slice(-3).map((p,i)=>(
              <div key={i} style={{flex:1,aspectRatio:"3/4",borderRadius:10,overflow:"hidden",
                background:C.faint,border:`1px solid ${C.border}`}}>
                <img src={p.dataUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              </div>
            ))}
          </div>
        ) : (
          <div style={{height:70,display:"flex",alignItems:"center",
            fontSize:12,color:C.muted,fontFamily:"'Nunito Sans',sans-serif"}}>
            Add your first progress photos — Front, Side &amp; Back →
          </div>
        )}
        <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
          <span style={{fontSize:12,fontWeight:800,color:C.grape,fontFamily:"'Nunito Sans',sans-serif"}}>View all ›</span>
        </div>
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREEN: BODY WEIGHT — graph + daily input + log table
// ═══════════════════════════════════════════════════════════════
function BodyWeightScreen({userId, onBack}) {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [kg, setKg]           = React.useState("");
  const [saved, setSaved]     = React.useState(false);
  const [timeframe, setTimeframe] = React.useState("1M");

  // Load from Supabase (or localStorage fallback)
  React.useEffect(()=>{
    async function load() {
      if (userId) {
        const to   = new Date().toISOString().split("T")[0];
        const from = new Date(Date.now()-365*24*60*60*1000).toISOString().split("T")[0];
        const { data } = await getWeightLogs(userId, from, to);
        setEntries(data||[]);
      } else {
        // localStorage fallback when no userId
        try { setEntries(JSON.parse(localStorage.getItem("gainr_weights")||"[]")); } catch{}
      }
      setLoading(false);
    }
    load();
  },[userId]);

  async function saveWeight() {
    if (!kg || isNaN(parseFloat(kg))) return;
    const today = new Date().toISOString().split("T")[0];
    const newKg  = parseFloat(kg);
    if (userId) {
      const { data } = await logWeight(userId, today, newKg);
      if (data) {
        setEntries(prev=>[...prev.filter(e=>e.log_date!==today),
          {log_date:today,weight_kg:newKg}].sort((a,b)=>a.log_date.localeCompare(b.log_date)));
      }
    } else {
      // localStorage fallback
      const updated = [...entries.filter(e=>e.log_date!==today),
        {log_date:today,weight_kg:newKg}].sort((a,b)=>a.log_date.localeCompare(b.log_date));
      setEntries(updated);
      localStorage.setItem("gainr_weights",JSON.stringify(updated));
    }
    setSaved(true); setKg("");
    setTimeout(()=>setSaved(false),2500);
  }

  async function removeEntry(date) {
    if (userId) await deleteWeightLog(userId, date);
    else {
      const updated = entries.filter(e=>e.log_date!==date);
      localStorage.setItem("gainr_weights",JSON.stringify(updated));
    }
    setEntries(prev=>prev.filter(e=>e.log_date!==date));
  }

  // Filter by timeframe
  const TF_DAYS = {"1W":7,"2W":14,"1M":30,"3M":90,"6M":180,"1Y":365};
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - (TF_DAYS[timeframe]||30));
  const filtered = entries.filter(e=>new Date(e.log_date)>=cutoff);
  const vals = filtered.map(e=>e.weight_kg);
  const avg  = vals.length ? Math.round((vals.reduce((a,b)=>a+b,0)/vals.length)*10)/10 : null;
  const minV = vals.length ? Math.min(...vals) : 0;
  const maxV = vals.length ? Math.max(...vals) : 100;
  const range = maxV - minV || 1;

  // SVG graph
  const W=320, H=90, PL=8, PR=8, PT=8, PB=4;
  const graphPts = filtered.map((e,i)=>{
    const x = PL + (i/(Math.max(filtered.length-1,1)))*(W-PL-PR);
    const y = PT + (1-((e.weight_kg-minV)/range))*(H-PT-PB);
    return {x,y,e};
  });
  const pathD = graphPts.map((p,i)=>`${i===0?"M":"L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");

  const fmtDate = d => {
    const dt = new Date(d+"T00:00:00");
    return dt.toLocaleDateString("en-AU",{day:"numeric",month:"short"});
  };

  return (
    <div style={{animation:"fadeUp 0.4s ease both"}}>
      <button onClick={onBack}
        style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",
          color:C.muted,fontWeight:700,fontSize:13,cursor:"pointer",marginBottom:16,padding:0}}>
        ← My Progress
      </button>

      <div style={{fontFamily:"'Righteous',cursive",fontSize:28,color:C.ink,marginBottom:20}}>Body Weight</div>

      {/* Log weight input */}
      <Card style={{marginBottom:14,border:`1.5px solid ${C.sky}30`,background:`${C.sky}06`}}>
        <SLabel>Log Today's Weight</SLabel>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <div style={{flex:1,position:"relative"}}>
            <input type="number" step="0.1" placeholder="75.0" value={kg}
              onChange={e=>{setKg(e.target.value);setSaved(false);}}
              onKeyDown={e=>e.key==="Enter"&&saveWeight()}
              style={{width:"100%",padding:"13px 40px 13px 14px",borderRadius:12,
                border:`1.5px solid ${kg?C.sky:C.border}`,background:C.bg,
                fontSize:18,fontWeight:800,color:C.ink,outline:"none",
                fontFamily:"'Nunito',sans-serif"}}/>
            <span style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",
              fontSize:13,fontWeight:700,color:C.muted,fontFamily:"'Nunito Sans',sans-serif"}}>kg</span>
          </div>
          <button onClick={saveWeight}
            style={{padding:"13px 20px",borderRadius:12,border:"none",cursor:"pointer",
              background:saved?C.mint:C.sky,color:saved?C.ink:"#fff",fontWeight:900,
              fontSize:15,fontFamily:"'Nunito',sans-serif",
              boxShadow:`3px 3px 0px ${C.ink}`,transition:"all 0.2s",flexShrink:0}}>
            {saved?"✓ Saved":"Log"}
          </button>
        </div>
        <div style={{fontSize:11,color:C.muted,marginTop:8,fontFamily:"'Nunito Sans',sans-serif"}}>
          {new Date().toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
        </div>
      </Card>

      {/* Graph card */}
      <Card style={{marginBottom:14}}>
        {/* Average + timeframe */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",
              letterSpacing:"0.08em",fontFamily:"'Nunito Sans',sans-serif",marginBottom:2}}>Average</div>
            <div style={{fontFamily:"'Righteous',cursive",fontSize:26,color:C.sky}}>
              {avg!=null?`${avg}kg`:"—"}
            </div>
          </div>
          {/* Timeframe pills */}
          <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
            {["1W","2W","1M","3M","6M","1Y"].map(tf=>(
              <button key={tf} onClick={()=>setTimeframe(tf)}
                style={{padding:"4px 10px",borderRadius:20,border:"none",cursor:"pointer",
                  fontSize:11,fontWeight:800,fontFamily:"'Nunito Sans',sans-serif",
                  background:timeframe===tf?C.sky:C.faint,
                  color:timeframe===tf?"#fff":C.muted,
                  transition:"all 0.15s"}}>
                {tf}
              </button>
            ))}
          </div>
        </div>

        {/* SVG Line graph */}
        {filtered.length >= 2 ? (
          <div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:H,overflow:"visible",display:"block"}}>
              {/* Grid lines */}
              {[0,0.5,1].map(t=>(
                <line key={t} x1={PL} x2={W-PR}
                  y1={PT+(1-t)*(H-PT-PB)} y2={PT+(1-t)*(H-PT-PB)}
                  stroke={C.border} strokeWidth="1"/>
              ))}
              {/* Y labels */}
              {[0,0.5,1].map(t=>(
                <text key={t} x={PL-2} y={PT+(1-t)*(H-PT-PB)+3}
                  textAnchor="end" fontSize="8" fill={C.muted} fontFamily="'Nunito Sans',sans-serif">
                  {Math.round(minV+t*range*10)/10}
                </text>
              ))}
              {/* Line */}
              <path d={pathD} fill="none" stroke={C.sky} strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"/>
              {/* Average line */}
              {avg&&<line x1={PL} x2={W-PR}
                y1={PT+(1-((avg-minV)/range))*(H-PT-PB)}
                y2={PT+(1-((avg-minV)/range))*(H-PT-PB)}
                stroke={C.sunshine} strokeWidth="1.5" strokeDasharray="4 3"/>}
              {/* Dots */}
              {graphPts.map((p,i)=>(
                <circle key={i} cx={p.x} cy={p.y} r="3.5"
                  fill={C.sky} stroke="#fff" strokeWidth="1.5"/>
              ))}
            </svg>
            {/* X axis labels */}
            <div style={{display:"flex",justifyContent:"space-between",marginTop:4,
              paddingLeft:PL,paddingRight:PR}}>
              {[filtered[0], filtered[Math.floor(filtered.length/2)], filtered[filtered.length-1]]
                .filter(Boolean).map((e,i)=>(
                <span key={i} style={{fontSize:9,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontWeight:600}}>
                  {fmtDate(e.date)}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div style={{height:90,display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:13,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontStyle:"italic"}}>
            Log at least 2 entries to see your trend graph
          </div>
        )}
      </Card>

      {/* Log table */}
      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{padding:"14px 16px 10px",display:"flex",alignItems:"center",justifyContent:"space-between",
          borderBottom:`1px solid ${C.border}`}}>
          <SLabel style={{margin:0}}>Weight Log</SLabel>
          <span style={{fontSize:11,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",fontWeight:600}}>
            {entries.length} entries
          </span>
        </div>
        {entries.length===0 ? (
          <div style={{padding:"24px 16px",textAlign:"center",fontSize:13,color:C.muted,
            fontFamily:"'Nunito Sans',sans-serif"}}>No entries yet — log your first weight above</div>
        ) : (
          <div>
            {/* Header */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 80px 36px",
              padding:"8px 16px",background:C.faint,
              borderBottom:`1px solid ${C.border}`}}>
              {["Date","KG",""].map(h=>(
                <div key={h} style={{fontSize:10,fontWeight:800,color:C.muted,
                  textTransform:"uppercase",letterSpacing:"0.08em",
                  fontFamily:"'Nunito Sans',sans-serif",
                  textAlign:h==="KG"?"center":"left"}}>{h}</div>
              ))}
            </div>
            {[...entries].sort((a,b)=>b.log_date.localeCompare(a.log_date)).map((e,i)=>(
              <div key={e.date} style={{display:"grid",gridTemplateColumns:"1fr 80px 36px",
                alignItems:"center",padding:"11px 16px",
                borderBottom:i<entries.length-1?`1px solid ${C.border}`:"none",
                background:i===0?`${C.sky}06`:"transparent"}}>
                <span style={{fontSize:13,fontWeight:i===0?800:600,color:i===0?C.ink:C.muted,
                  fontFamily:"'Nunito Sans',sans-serif"}}>
                  {fmtDate(e.log_date)}{i===0?" · Today":""}
                </span>
                <span style={{fontSize:14,fontWeight:800,color:i===0?C.sky:C.ink,
                  fontFamily:"'Righteous',cursive",textAlign:"center"}}>
                  {e.weight_kg}kg
                </span>
                <button onClick={()=>removeEntry(e.log_date)}
                  style={{background:"none",border:"none",cursor:"pointer",
                    color:C.muted,fontSize:16,padding:"2px",opacity:0.5}}>×</button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREEN: PHOTOS — Front/Side/Back grid + landscape compare
// ═══════════════════════════════════════════════════════════════
function PhotosScreen({userId, onBack}) {
  const [photos,    setPhotos]    = React.useState([]);
  const [loading,   setLoading]   = React.useState(true);
  const [landscape, setLandscape] = React.useState(false);
  const [compareA,  setCompareA]  = React.useState(0);
  const [compareB,  setCompareB]  = React.useState(0);
  const [compareType, setCompareType] = React.useState("front");
  const [uploading, setUploading] = React.useState(false);
  const fileRef     = React.useRef();
  const [pendingSlot, setPendingSlot] = React.useState(null);

  // Load photos from Supabase (or localStorage fallback)
  React.useEffect(()=>{
    async function load() {
      if (userId) {
        const { data } = await getPhotos(userId);
        // Normalise to {date, type, dataUrl/public_url}
        setPhotos((data||[]).map(p=>({
          date:    p.photo_date,
          type:    p.photo_type,
          dataUrl: p.public_url,
        })));
      } else {
        try { setPhotos(JSON.parse(localStorage.getItem("gainr_photos")||"[]")); } catch{}
      }
      setLoading(false);
    }
    load();
  },[userId]);

  // Detect orientation
  React.useEffect(()=>{
    const check = ()=>setLandscape(window.innerWidth>window.innerHeight);
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return ()=>{ window.removeEventListener("resize",check); window.removeEventListener("orientationchange",check); };
  },[]);

  // Group photos by week (date bucket) — get unique dates
  const dates = [...new Set(photos.map(p=>p.date))].sort((a,b)=>b.localeCompare(a));

  function getPhoto(date, type) {
    return photos.find(p=>p.date===date&&p.type===type)||null;
  }

  function openPicker(date, type) {
    setPendingSlot({date,type});
    fileRef.current.click();
  }

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file || !pendingSlot) return;
    setUploading(true);
    e.target.value = "";

    if (userId) {
      // Upload to Supabase Storage
      const { data } = await uploadPhoto(userId, pendingSlot.date, pendingSlot.type, file);
      if (data) {
        setPhotos(prev => [
          ...prev.filter(p=>!(p.date===pendingSlot.date&&p.type===pendingSlot.type)),
          { date:pendingSlot.date, type:pendingSlot.type, dataUrl:data.public_url }
        ]);
      }
    } else {
      // localStorage fallback
      const reader = new FileReader();
      reader.onload = ev => {
        const newPhoto = {date:pendingSlot.date, type:pendingSlot.type, dataUrl:ev.target.result};
        const updated = [...photos.filter(p=>!(p.date===pendingSlot.date&&p.type===pendingSlot.type)), newPhoto];
        setPhotos(updated);
        localStorage.setItem("gainr_photos", JSON.stringify(updated));
      };
      reader.readAsDataURL(file);
    }
    setUploading(false);
  }

  function addNewDate() {
    const today = new Date().toISOString().split("T")[0];
    if (!dates.includes(today)) {
      // Adding a new date automatically — user then taps + on each slot
      const placeholder = {date:today,type:"__placeholder__",dataUrl:""};
      const updated = [...photos, placeholder];
      setPhotos(updated);
      localStorage.setItem("gainr_photos", JSON.stringify(updated));
    }
  }

  const fmtDate = d => {
    const dt = new Date(d+"T00:00:00");
    return dt.toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"2-digit"});
  };

  const TYPES = ["front","side","back"];
  const TYPE_LABELS = {front:"Front",side:"Side",back:"Back"};

  // ── LANDSCAPE COMPARE MODE ──
  if (landscape && dates.length >= 1) {
    const allDates = dates;
    const photoA = getPhoto(allDates[compareA]||allDates[0], compareType);
    const photoB = getPhoto(allDates[compareB]||allDates[0], compareType);
    return (
      <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,
        background:C.ink,zIndex:300,display:"flex",flexDirection:"column"}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"10px 16px",borderBottom:`1px solid rgba(255,255,255,0.1)`}}>
          <button onClick={()=>setLandscape(false)}
            style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",
              fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"'Nunito',sans-serif"}}>
            ✕
          </button>
          <div style={{display:"flex",gap:6}}>
            {TYPES.map(t=>(
              <button key={t} onClick={()=>setCompareType(t)}
                style={{padding:"4px 12px",borderRadius:20,border:"none",cursor:"pointer",
                  fontSize:11,fontWeight:800,fontFamily:"'Nunito Sans',sans-serif",
                  background:compareType===t?C.coral:"rgba(255,255,255,0.1)",
                  color:compareType===t?"#fff":"rgba(255,255,255,0.5)"}}>
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          <div style={{width:40}}/>
        </div>
        {/* Side by side photos */}
        <div style={{flex:1,display:"flex",gap:2,overflow:"hidden"}}>
          {[[compareA,setCompareA],[compareB,setCompareB]].map(([idx,setIdx],side)=>(
            <div key={side} style={{flex:1,display:"flex",flexDirection:"column",
              position:"relative",overflow:"hidden"}}>
              {(getPhoto(allDates[idx]||allDates[0],compareType)) ? (
                <img src={getPhoto(allDates[idx]||allDates[0],compareType).dataUrl}
                  alt="" style={{flex:1,objectFit:"cover",objectPosition:"top"}}/>
              ) : (
                <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",
                  background:"rgba(255,255,255,0.05)",fontSize:12,
                  color:"rgba(255,255,255,0.3)",fontFamily:"'Nunito Sans',sans-serif"}}>
                  No photo
                </div>
              )}
              {/* Date label */}
              <div style={{padding:"6px 10px",background:"rgba(0,0,0,0.7)",
                display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <button onClick={()=>setIdx(i=>Math.min(i+1,allDates.length-1))}
                  style={{background:"none",border:"none",color:"rgba(255,255,255,0.5)",
                    fontSize:18,cursor:"pointer",padding:0}}>‹</button>
                <span style={{fontSize:11,fontWeight:700,color:"#fff",
                  fontFamily:"'Nunito Sans',sans-serif"}}>
                  {fmtDate(allDates[idx]||allDates[0])}
                </span>
                <button onClick={()=>setIdx(i=>Math.max(i-1,0))}
                  style={{background:"none",border:"none",color:"rgba(255,255,255,0.5)",
                    fontSize:18,cursor:"pointer",padding:0}}>›</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center",padding:"6px 0",fontSize:10,
          color:"rgba(255,255,255,0.25)",fontFamily:"'Nunito Sans',sans-serif"}}>
          Rotate back to portrait to exit compare mode
        </div>
      </div>
    );
  }

  return (
    <div style={{animation:"fadeUp 0.4s ease both"}}>
      <input ref={fileRef} type="file" accept="image/*" capture="environment"
        onChange={handleFile} style={{display:"none"}}/>

      <button onClick={onBack}
        style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",
          color:C.muted,fontWeight:700,fontSize:13,cursor:"pointer",marginBottom:16,padding:0}}>
        ← My Progress
      </button>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
        <div style={{fontFamily:"'Righteous',cursive",fontSize:28,color:C.ink}}>Progress Photos</div>
        <button onClick={addNewDate}
          style={{padding:"8px 16px",borderRadius:12,border:"none",cursor:"pointer",
            background:C.grape,color:"#fff",fontWeight:800,fontSize:13,
            boxShadow:`3px 3px 0px ${C.ink}`,fontFamily:"'Nunito',sans-serif"}}>
          + New
        </button>
      </div>
      <p style={{fontSize:12,color:C.muted,marginBottom:20,fontFamily:"'Nunito Sans',sans-serif",lineHeight:1.6}}>
        📱 Rotate to landscape to compare photos side by side
      </p>

      {/* Column headers */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:8}}>
        {TYPES.map(t=>(
          <div key={t} style={{textAlign:"center",fontSize:11,fontWeight:800,color:C.muted,
            textTransform:"uppercase",letterSpacing:"0.1em",
            fontFamily:"'Nunito Sans',sans-serif"}}>{TYPE_LABELS[t]}</div>
        ))}
      </div>

      {/* Photo rows */}
      {dates.filter(d=>d!=="__placeholder__").length===0 ? (
        <div style={{textAlign:"center",padding:"48px 20px",fontSize:13,color:C.muted,
          fontFamily:"'Nunito Sans',sans-serif",lineHeight:1.7}}>
          Tap <strong style={{color:C.grape}}>+ New</strong> to start your first progress photo check-in.<br/>
          Take Front, Side and Back photos for best results.
        </div>
      ) : (
        dates.filter(d=>{
          // Only show dates that have at least one real photo or are today
          const today = new Date().toISOString().split("T")[0];
          return d===today || TYPES.some(t=>getPhoto(d,t));
        }).map(date=>(
          <div key={date} style={{marginBottom:16}}>
            {/* Date label */}
            <div style={{fontSize:12,fontWeight:800,color:C.muted,marginBottom:8,
              fontFamily:"'Nunito Sans',sans-serif",letterSpacing:"0.04em"}}>
              {fmtDate(date)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {TYPES.map(type=>{
                const photo = getPhoto(date,type);
                return (
                  <button key={type} onClick={()=>openPicker(date,type)}
                    style={{aspectRatio:"3/4",borderRadius:12,overflow:"hidden",
                      border:`1.5px solid ${photo?C.grape+"40":C.border}`,
                      background:photo?"transparent":C.faint,
                      cursor:"pointer",display:"flex",alignItems:"center",
                      justifyContent:"center",padding:0,position:"relative"}}>
                    {photo ? (
                      <>
                        <img src={photo.dataUrl} alt={type}
                          style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}}/>
                        <div style={{position:"absolute",bottom:4,right:4,
                          background:"rgba(123,79,255,0.85)",borderRadius:6,
                          padding:"2px 5px",fontSize:9,color:"#fff",fontWeight:700,
                          fontFamily:"'Nunito Sans',sans-serif"}}>✎</div>
                      </>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        <span style={{fontSize:22,color:C.muted,opacity:0.5}}>+</span>
                        <span style={{fontSize:9,color:C.muted,fontFamily:"'Nunito Sans',sans-serif",
                          fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>{TYPE_LABELS[type]}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ROOT APP — updated with 4-tab nav + progress screens
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ROOT APP — Supabase Auth session management
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [screen,    setScreen]    = useState("landing");
  const [subScreen, setSubScreen] = useState(null);
  const [profile,   setProfile]   = useState(null);
  const [userId,    setUserId]    = useState(null);
  const [planId,    setPlanId]    = useState(null);
  const [workout,   setWorkout]   = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [restoredSessions, setRestoredSessions] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const weekNum = 1;

  const {macros, sessions, tdee} = useMemo(()=>{
    if (!profile) return {macros:null, sessions:[], tdee:null};
    const t = calcTDEE(profile);
    const m = profile.macro_mode==="manual" && profile.custom_calories
      ? { calories:parseInt(profile.custom_calories),
          proteinG:parseInt(profile.custom_protein)||Math.round(parseFloat(profile.weight_kg||70)*2),
          carbG:parseInt(profile.custom_carbs)||120,
          fatG:parseInt(profile.custom_fats)||60,
          proteinPerMeal:Math.round((parseInt(profile.custom_protein)||120)/4),
          deficit:null, surplus:null }
      : calcMacros(profile, profile.goal, t);
    return {macros:m, sessions:restoredSessions||buildSchedule(profile), tdee:t};
  }, [profile, restoredSessions]);

  // Load profile data for a given auth user
  async function loadUserProfile(authUser) {
    if (!authUser?.email) {
      setScreen("landing");
      setAuthLoading(false);
      return;
    }
    try {
      const { data, error } = await getProfileByEmail(authUser.email);
      if (data?.onboarding_complete) {
        // Returning user with complete profile
        const profileData = {
          ...data,
          custom_protein: data.custom_protein_g,
          custom_carbs:   data.custom_carbs_g,
          custom_fats:    data.custom_fats_g,
        };
        setProfile(profileData);
        setUserId(data.id);
        try {
          const { data: plan } = await getActivePlan(data.id);
          if (plan?.id) {
            setPlanId(plan.id);
            if (plan.sessions) {
              setRestoredSessions(
                Array.isArray(plan.sessions) ? plan.sessions
                  : JSON.parse(plan.sessions||"[]")
              );
            }
          }
        } catch(e) { console.warn("Plan load failed:", e); }
        setScreen("plan");
      } else if (data && !data.onboarding_complete) {
        // Profile exists but onboarding not complete
        setProfile(data);
        setUserId(data.id);
        setScreen("onboarding");
      } else {
        // No profile yet — new user, go to onboarding
        // Pre-set email from auth so onboarding can use it
        setProfile({ email: authUser.email });
        setScreen("onboarding");
      }
    } catch(e) {
      console.warn("Profile load error:", e);
      // Treat as new user — send to onboarding
      setProfile({ email: authUser.email });
      setScreen("onboarding");
    }
    setAuthLoading(false);
  }

  // Handle Supabase auth callback from email links
  // When redirected back from confirmation/reset email, the token is in the URL hash
  useEffect(()=>{
    const hash = window.location.hash;
    if (hash && (hash.includes('access_token') || hash.includes('error'))) {
      // Let Supabase client pick up the token from the URL
      sb.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          // Clear the hash from URL so it doesn't persist
          window.history.replaceState(null, '', window.location.pathname);
        }
      }).catch(e => {
        console.warn('Session from URL hash failed:', e);
        setAuthLoading(false);
        setScreen('landing');
      });
    }
  }, []);

  // Safety net — never leave spinner stuck forever
  useEffect(()=>{
    const t = setTimeout(()=>{
      setAuthLoading(prev => {
        if (prev) {
          setScreen("landing");
          return false;
        }
        return prev;
      });
    }, 5000); // 5 seconds max
    return ()=>clearTimeout(t);
  }, []);
  useEffect(()=>{
    const { data: { subscription } } = sb.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        await loadUserProfile(session.user);
      } else {
        // Signed out or no session
        setProfile(null);
        setUserId(null);
        setPlanId(null);
        setRestoredSessions(null);
        setWorkout(null);
        setScreen("landing");
        setAuthLoading(false);
      }
    });
    return ()=>subscription.unsubscribe();
  }, []);

  // Handler called when user signs in or signs up via landing screen
  async function handleSignedIn(session, user, isNewSignup) {
    if (isNewSignup) {
      // New signup — go through onboarding
      setScreen("onboarding");
    } else {
      await loadUserProfile(user);
    }
  }

  async function handleOnboardingComplete(profileData) {
    // Merge into profile state immediately with all fields
    setProfile(prev=>({...prev,...profileData}));
    try {
      // Get current auth user
      const { data: { user }, error: userError } = await sb.auth.getUser();
      if (userError) {
        console.error("getUser failed:", userError);
        alert("Auth error: " + userError.message);
        setScreen("plan");
        return;
      }
      if (!user) {
        console.error("No auth user found");
        alert("No authenticated user found. Please sign in again.");
        setScreen("landing");
        return;
      }

      const dbProfile = {
        email:             profileData.email || user?.email,
        age:               profileData.age,
        sex:               profileData.sex,
        height_cm:         profileData.height_cm,
        weight_kg:         profileData.weight_kg,
        goal_weight_kg:    profileData.goal_weight_kg,
        body_type:         profileData.body_type,
        cycle_phase:       profileData.cycle_phase,
        goal:              profileData.goal,
        activity_level:    profileData.activity_level,
        training_history:  profileData.training_history,
        training_days:     profileData.training_days,
        training_type:     profileData.training_type,
        training_style:    profileData.training_style,
        injuries:          profileData.injuries||"none",
        macro_mode:        profileData.macro_mode||"auto",
        custom_calories:   profileData.custom_calories ? parseInt(profileData.custom_calories) : null,
        custom_protein_g:  profileData.custom_protein  ? parseInt(profileData.custom_protein)  : null,
        custom_carbs_g:    profileData.custom_carbs    ? parseInt(profileData.custom_carbs)    : null,
        custom_fats_g:     profileData.custom_fats     ? parseInt(profileData.custom_fats)     : null,
        onboarding_complete: true,
      };

      console.log("Saving profile:", dbProfile);
      const { row, error: profileError } = await upsertProfile(dbProfile);
      console.log("Profile save result:", row, profileError);

      if (profileError) {
        alert("Profile save error: " + profileError.message + "\nCode: " + profileError.code);
      }

      if (row?.id) {
        setUserId(row.id);
        const t = calcTDEE(profileData);
        const m = calcMacros(profileData, profileData.goal, t);
        const s = buildSchedule(profileData);
        setRestoredSessions(s); // Set immediately so PlanScreen has exercises
        const { data: planRow, error: planError } = await savePlan(row.id, {
          macros:m, tdee:t, goal:profileData.goal, sessions:s
        });
        console.log("Plan save result:", planRow, planError);
        if (planError) alert("Plan save error: " + planError.message);
        if (planRow?.id) setPlanId(planRow.id);
      } else {
        alert("Profile row was not returned. Check Supabase RLS policies.");
      }
    } catch(e) {
      console.error("handleOnboardingComplete error:", e);
      alert("Unexpected error: " + e.message);
    }
    setScreen("plan");
  }

  async function handleSignOut() {
    await sb.auth.signOut();
    // onAuthStateChange will handle state reset
  }

  // Show loading spinner while checking auth session
  if (authLoading) {
    return (
      <div style={{minHeight:"100vh",background:C.ink,display:"flex",
        alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
        <style>{GLOBAL}</style>
        <div style={{width:36,height:36,border:`3px solid rgba(255,255,255,0.1)`,
          borderTopColor:C.coral,borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",
          fontFamily:"'Nunito Sans',sans-serif",fontWeight:600}}>Loading GAINR…</div>
      </div>
    );
  }

  if (screen==="landing")    return <LandingScreen onSignedIn={handleSignedIn}/>;
  if (screen==="onboarding") return (
    <OnboardingScreen
      email={profile?.email||""}
      onComplete={handleOnboardingComplete}/>
  );

  const NAV = [
    {s:"plan",     icon:"🏠", label:"Plan"},
    {s:"progress", icon:"📈", label:"Progress"},
    {s:"checkin",  icon:"📋", label:"Check-in"},
    {s:"settings", icon:"⚙️", label:"Settings"},
  ];

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"'Nunito',sans-serif"}}>
      <style>{GLOBAL}</style>

      {/* Top header */}
      <div style={{background:"rgba(255,248,240,0.97)",backdropFilter:"blur(16px)",
        padding:"12px 20px",borderBottom:`1px solid ${C.border}`,
        display:"flex",alignItems:"center",justifyContent:"space-between",
        position:"sticky",top:0,zIndex:90}}>
        <Logo size={16}/>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <Pill label={(profile?.goal||"plan").replace("_"," ")} color={C.grape}/>
          <span style={{fontSize:11,fontWeight:700,color:C.muted,
            fontFamily:"'Nunito Sans',sans-serif"}}>Wk {weekNum}</span>
        </div>
      </div>

      {/* Content */}
      <div style={{maxWidth:600,margin:"0 auto",padding:"24px 20px 100px"}}>

        {screen==="plan"&&!workout&&(
          <PlanScreen profile={profile} macros={macros} sessions={sessions}
            weekNum={weekNum} onWorkout={async s=>{
              setWorkout(s);
              if (userId) {
                try {
                  const { data } = await startWorkoutSession(userId, planId, s);
                  if (data?.id) setSessionId(data.id);
                } catch(e) { console.warn("Session start failed:", e); }
              }
              setScreen("workout");
            }}/>
        )}
        {screen==="workout"&&workout&&(
          <WorkoutScreen session={workout} userId={userId} sessionId={sessionId}
            onBack={()=>{setWorkout(null);setSessionId(null);setScreen("plan");}}/>
        )}

        {screen==="progress"&&!subScreen&&(
          <ProgressScreen userId={userId} onNav={s=>setSubScreen(s)}/>
        )}
        {screen==="progress"&&subScreen==="bodyweight"&&(
          <BodyWeightScreen userId={userId} onBack={()=>setSubScreen(null)}/>
        )}
        {screen==="progress"&&subScreen==="photos"&&(
          <PhotosScreen userId={userId} onBack={()=>setSubScreen(null)}/>
        )}

        {screen==="checkin"&&(
          <div style={{textAlign:"center",padding:"60px 20px",animation:"fadeUp 0.4s ease both"}}>
            <div style={{fontSize:48,marginBottom:16}}>📋</div>
            <div style={{fontFamily:"'Righteous',cursive",fontSize:24,color:C.ink,marginBottom:8}}>Weekly Check-in</div>
            <div style={{fontSize:14,color:C.muted,lineHeight:1.7,maxWidth:280,margin:"0 auto"}}>
              Coming in the next update. Log weight daily in Progress → Body Weight for now.
            </div>
          </div>
        )}

        {screen==="settings"&&(
          <div style={{animation:"fadeUp 0.4s ease both"}}>
            <h1 style={{fontFamily:"'Righteous',cursive",fontSize:30,color:C.ink,marginBottom:4}}>Settings</h1>
            <p style={{fontSize:13,color:C.muted,marginBottom:24,lineHeight:1.6}}>Manage your account.</p>

            {/* Account card */}
            <Card style={{marginBottom:14}}>
              <SLabel>Account</SLabel>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                <div style={{position:"relative",width:46,height:46,flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:2,width:46,height:46,
                    background:C.coral,borderRadius:12}}/>
                  <div style={{position:"absolute",top:0,left:0,width:46,height:46,
                    background:C.ink,borderRadius:12,display:"flex",
                    alignItems:"center",justifyContent:"center"}}>
                    <span style={{fontFamily:"'Righteous',cursive",fontSize:22,color:C.sunshine}}>G</span>
                  </div>
                </div>
                <div>
                  <div style={{fontWeight:800,fontSize:14,color:C.ink}}>GAINR Account</div>
                  <div style={{fontSize:12,color:C.muted}}>{profile?.email||""}</div>
                </div>
              </div>
              <Btn variant="ghost" onClick={handleSignOut} fullWidth>Sign Out</Btn>
            </Card>

            {/* Profile summary */}
            {profile&&(
              <Card style={{marginBottom:14}}>
                <SLabel>Your Profile</SLabel>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[
                    {l:"Goal",     v:(profile.goal||"").replace("_"," ")},
                    {l:"Age",      v:profile.age?`${profile.age} yrs`:"—"},
                    {l:"Weight",   v:profile.weight_kg?`${profile.weight_kg}kg`:"—"},
                    {l:"Training", v:profile.training_days?`${profile.training_days}×/week`:"—"},
                    {l:"Level",    v:profile.training_history||"—"},
                    {l:"Injuries", v:(profile.injuries||"none").replace("_"," ")},
                  ].map(item=>(
                    <div key={item.l} style={{padding:"8px 10px",background:C.faint,borderRadius:8}}>
                      <div style={{fontSize:10,fontWeight:700,color:C.muted,
                        textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:2}}>{item.l}</div>
                      <div style={{fontSize:13,fontWeight:800,color:C.ink,
                        textTransform:"capitalize"}}>{item.v}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <div style={{textAlign:"center",padding:"12px 0",
              fontSize:11,color:C.muted,fontWeight:600}}>
              GAINR V3.1 · Powered by Supabase + Anthropic
            </div>
          </div>
        )}
      </div>

      {/* 4-tab Bottom nav */}
      <nav style={{position:"fixed",bottom:0,left:0,right:0,zIndex:100,
        background:"rgba(255,248,240,0.97)",backdropFilter:"blur(20px)",
        borderTop:`2px solid ${C.border}`,padding:"8px 16px 20px",
        display:"flex",justifyContent:"space-around"}}>
        {NAV.map(({s,icon,label})=>{
          const active = screen===s||(screen==="workout"&&s==="plan");
          return (
            <button key={s} onClick={()=>{
              setSubScreen(null);
              if(s==="plan") setWorkout(null);
              setScreen(s);
            }}
              style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,
                background:"none",border:"none",minWidth:52}}>
              <div style={{width:40,height:40,borderRadius:12,display:"flex",alignItems:"center",
                justifyContent:"center",fontSize:18,transition:"all 0.2s",
                background:active?C.ink:"transparent",
                boxShadow:active?`2px 2px 0px ${C.coral}`:"none"}}>
                {icon}
              </div>
              <span style={{fontSize:10,fontWeight:active?800:600,
                color:active?C.ink:C.muted,
                fontFamily:"'Nunito Sans',sans-serif",letterSpacing:"0.03em"}}>{label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
